/* Percorso file: \src\pages\Messaging.jsx */

// =============================================================================
// LIVE CHAT — la pagina `Messaging`
// =============================================================================
// Ricostruita nel Blocco 4 del cantiere LIVE CHAT, in due commit:
//
//   4a — LA FUNZIONE (materia di riservatezza)
//   4b — LA VESTE (doc 07 §14)
//
// -----------------------------------------------------------------------------
// LA FUNZIONE, in una riga per punto (4a)
// -----------------------------------------------------------------------------
//   • L'ELENCO È UGUALE PER OGNI RUOLO — `lettori` array-contains l'uid del
//     LOGIN, più il vincolo d'anno (due query, v. `VIA_ANNO_CON_IN`). I quattro rami
//     per ruolo spariscono: le liste le ha già composte il backend pesando
//     ruolo, tessere e anno (fondazione, Blocco 1).
//   • LA RUBRICA VIENE PRIMA DELL'ELENCO. `GET /chat/directory` porta l'anno
//     operativo (che il client NON può leggere da Firestore: `clubConfig` è in
//     default-deny) e le persone. Senza anno l'elenco non parte.
//   • I MESSAGGI SI ORDINANO SU `quando`, non su `timestamp`: il campo vecchio
//     non esiste più sui messaggi nuovi, e una query che ordina su un campo
//     assente ESCLUDE i documenti — i messaggi nuovi non comparivano affatto.
//   • IL PALLINO LO AZZERA IL BACKEND (`POST /chat/letta`). Il client non
//     scrive più su `chats`: il recinto del Blocco 3 glielo nega.
//   • LE DUE IDENTITÀ (D1). La bolla sta a destra se l'ha scritta chi ha fatto
//     l'accesso (`autoreReale`); il NOME che vi compare è quello del soggetto
//     MOSTRATO (il bambino, quando un tutore scrive a nome suo), risolto sulla
//     rubrica. Mai `autoreReale` a schermo, mai un recapito.
//   • VIA IL MOTORE `delegatedUser`. Il legame tutore→figlio viene dal dato
//     (`linkedProfiles`, sede unica `idsDeiFigli`) e lo VERIFICA il server sui
//     `guardians` del figlio; non da `localStorage`.
//   • LA FINESTRA EMAIL HA UN GATE DI RUOLO VERO e prende gli indirizzi da una
//     rotta di backend che, per un minore, emette l'email del TUTORE (D6).
//
// -----------------------------------------------------------------------------
// LA VESTE (4b) — doc 07 §14
// -----------------------------------------------------------------------------
//   • Tre pannelli sopra i 768px (elenco | conversazione | info partecipanti),
//     una schermata per volta sotto: §14.2, deroga dichiarata.
//   • Bolle stile Telegram, separatori di data, raggruppamento per mittente,
//     spunte di lettura verdi dal set icone: §14.4.
//   • Il ruolo leggibile sotto OGNI nome: §14.5.
//   • Barra admin separata e invisibile ai soci; «Admin staff» sempre visibile
//     a tutti: §14.6.
//   • Nessuna emoji: le icone vengono da `react-icons/fa`, già in casa.
//   • Nessun esadecimale: i colori vengono dai token del tema. Il foglio
//     `pages/Messaging.css` è iscritto in `FILE_CONFORMI` di check-theme.js.
//
// ⚠️ ALLEGATI: NESSUN PULSANTE. Il backend RIFIUTA gli allegati
// («Attachments are not supported yet»): la graffetta disabilitata della
// barra di composizione è stata TOLTA dalla vista (cantiere LIVECHAT-MENU,
// decisione del committente del 5/10). La parte del server non è toccata.
//
// REAZIONI (cantiere LIVECHAT-REAZIONI, Fetta 3b): accese, solo nei gruppi di
// squadra e nelle dirette. Le pastiglie sotto la bolla si disegnano dal
// DOCUMENTO del messaggio (`reazioni`, che arriva già con `onSnapshot`); si
// reagisce dalla voce «React» del menu «⋮» (POST /messages/react); il tocco
// su una pastiglia apre l'elenco dei nomi (GET /messages/reactions). Il
// `reale` delle voci non si mostra MAI.
// =============================================================================

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  where
} from "firebase/firestore";

// Le icone vengono dal set (doc 07 §5): niente emoji, che il §14.1 vieta.
// `react-icons/fa` è già nel progetto e già in uso (NavBand, SeasonStatusBar,
// le home): non si aggiunge una dipendenza, si usa quella che c'è.
import {
  FaBullhorn,
  FaCheck,
  FaCheckDouble,
  FaEllipsisV,
  FaEnvelope,
  FaFire,
  FaFutbol,
  FaGlobe,
  FaGrinTears,
  FaHeart,
  FaInfoCircle,
  FaPlus,
  FaSadCry,
  FaThumbsUp,
  FaUsers
} from "react-icons/fa";

import "./Messaging.css";

// Le chiamate al backend passano tutte da `api`, che monta l'ID token su ogni
// richiesta: l'identità di chi agisce la dice il token, mai il corpo.
import { api, TIMEOUT_LUNGO_MS, TIMEOUT_INVIO_MESSAGE } from "../api/api";
import AuthAvatar from "../components/AuthAvatar";

// TELAIO §7 — barra utente (§7.1) e banda di navigazione (§7.2) sono
// COMPONENTI CONDIVISI: gli stessi che HomeShell monta per tutte le altre
// pagine. Qui si montano direttamente, e non tramite HomeShell, perche' quel
// guscio porta con se' anche la colonna §4 (90%, massimo 460px) mentre questa
// pagina ha una deroga di layout DICHIARATA (§14.2: tre pannelli su desktop,
// colonna piena su mobile). E' lo stesso calco di HomeChild.jsx, che per un
// altro motivo monta NavBand da se'.
import UserBar from "../components/UserBar";
import NavBand from "../components/NavBand";

// La pastiglia categoria (doc 07 §2) è un componente condiviso: si USA, non si
// ricopia. Porta con sé il proprio foglio (components/AdminTools.css).
import { PastigliaCategoriaSeNota } from "../components/PastigliaCategoria";
import { normalizza } from "../utils/roleUtils";

// Gate unico dei ruoli: sede unica in auth/access.js (P0), qui si RIUSA.
// `canAccess` risponde a «possiede questo ruolo» — primo livello o tessera
// ACTIVE, vocabolario canonico — ed è lo stesso metro del backend
// (`possiedeRuoloCanonico(ruoliPosseduti(...))`).
//
// ⚠️ `isActingAs`/`currentRole` rispondono a un'ALTRA domanda: «quale cappello
// porto ADESSO». La dottrina del gate unico è dichiarata lì e qui si applica
// alla lettera: COMANDI → permesso (`canAccess`), VISTE E FILTRI → cappello
// (`isActingAs`). Il cappello NON è un permesso: non parte mai verso il
// backend, che continua a decidere dal token. Governa solo ciò che lo schermo
// mostra e offre.
import { canAccess, canonicalRole, isActingAs, roleLabel } from "../auth/access";

// Le squadre di UNA TESSERA — sede unica, `{ teams, allTeams }`. Serve a
// leggere le squadre del CAPPELLO senza ricopiarne la regola B.
import { squadreDellaTessera } from "../utils/squadre";

// LA REGOLA DI LETTURA DI `linkedProfiles` È UNA SOLA, e sta lì: la legge la
// home del genitore, la legge questa pagina. Non se ne fa una copia.
//
// ⚠️ È LA FACCIA DEBOLE DEL LEGAME, e qui è solo un SUGGERIMENTO per lo
// schermo: serve a proporre «a nome di quale figlio scrivo». La faccia
// autorevole è `guardians` sul documento del figlio, e la verifica il backend
// a ogni invio (`POST /messages` → `figliDiGenitore`). Un elenco stantio qui
// non produce un messaggio firmato male: produce un rifiuto del server, che si
// legge a schermo.
import { idsDeiFigli } from "../utils/famiglia";

// -----------------------------------------------------------------------------
// IL VOCABOLARIO DEL MODELLO NUOVO
// -----------------------------------------------------------------------------
// La sede unica di questi valori è il backend (`CHAT_TIPO_*`, `ID_CHAT_ANNUNCIO`
// in server.js): qui se ne tiene lo specchio perché il client deve saper
// leggere i documenti che quello scrive. Non è un secondo modello: sono le
// stesse quattro parole.
const TIPO_SQUADRA = "squadra";
const TIPO_RUOLO = "ruolo";
const TIPO_DIRETTA = "diretta";
const TIPO_ANNUNCIO = "annuncio";

// L'unico documento d'annuncio del club, per sempre: l'id è un fossile, e si
// ritrova con un `get()` per id. Serve al comando «Announcements» della barra
// admin quando l'annuncio non è ancora nato (nasce alla prima riga scritta).
const ID_ANNUNCIO = "announcement";

// MARCATORI NON-PERSONA: valori del campo `role` che non indicano un ruolo ma
// lo stato di un record. "Deleted Record" non è una persona a cui scrivere né
// un destinatario di posta (P6, decisione D2 del capomastro).
const RUOLI_NON_PERSONA = ["DELETED RECORD"];

// -----------------------------------------------------------------------------
// IL MITTENTE ISTITUZIONALE (decisione 2/9)
// -----------------------------------------------------------------------------
// Nei due canali del club — annuncio e ruolo — può scrivere SOLO un
// amministratore (`server.js`: 403 «Only admin can send announcement» / «Only
// admin can send to a role»): un annuncio è comunicazione del CLUB, non di una
// persona. La bolla si firma quindi col nome del club, chiunque abbia premuto
// invio. Risolve per costruzione anche il caso del SUPERADMIN, che non è
// `membro` di alcun gruppo e per un non-admin non compare in rubrica: in quei
// canali il suo nome non si guarda affatto.
//
// ⚠️ NON si riusa `titoloMostrato` («Announcements»): quello è il titolo della
// CHAT, non il nome del MITTENTE. Sono due cose diverse.
const MITTENTE_ISTITUZIONALE = "Queens Park SC";
const RUOLO_ISTITUZIONALE = "Club";

// Ripiego per una persona che la rubrica non conosce (una persona archiviata
// resta autrice di messaggi vecchi ma esce dalla rubrica). Mai «?», mai
// «Loading…»: doc 07 §14.10.
const PERSONA_SCONOSCIUTA = "Club member";

// -----------------------------------------------------------------------------
// LE SEI REAZIONI (cantiere LIVECHAT-REAZIONI, Fetta 3b)
// -----------------------------------------------------------------------------
// Le chiavi sono lo specchio di `REAZIONI_AMMESSE` in server.js, nello STESSO
// ordine (è anche l'ordine delle pastiglie: il documento è una mappa e non ne
// garantisce uno). ⚠️ `fire`, non `party`: la Fetta 3a l'ha rinominata, e il
// server risponde 400 a una chiave che non conosce.
// Icone tutte PIENE, scelta del committente: la propria si distingue SOLO
// per il bordo oro (doc 07 §14.8).
const REAZIONI = [
  { chiave: "thumbsUp", Icona: FaThumbsUp, etichetta: "Like" },
  { chiave: "heart", Icona: FaHeart, etichetta: "Love" },
  { chiave: "laugh", Icona: FaGrinTears, etichetta: "Laugh" },
  { chiave: "ball", Icona: FaFutbol, etichetta: "Ball" },
  { chiave: "fire", Icona: FaFire, etichetta: "Fire" },
  { chiave: "sad", Icona: FaSadCry, etichetta: "Sad" }
];

// Le pastiglie di un messaggio, dal DOCUMENTO: per ogni chiave con almeno una
// voce, il conteggio e se è «mia».
//  • CONTEGGIO = i `mostrato` distinti, come GET /messages/reactions (che
//    deduplica allo stesso modo, `trim` e vuoti esclusi compresi): due tutori
//    dello stesso bambino contano uno, e il numero coincide con le righe
//    dell'elenco.
//  • «MIA» = l'ho premuta io (`reale`) A NOME DEL SOGGETTO con cui scrivo ora
//    (`mostrato`). È la stessa coppia che il «togli» del backend cerca: il
//    gesto e il segno parlano la stessa lingua.
// ⚠️ Il `reale` qui si CONFRONTA e basta: non esce mai a schermo.
function pastiglieDelMessaggio(reazioni, mioId, soggetto) {
  const grezze = reazioni && typeof reazioni === "object" ? reazioni : {};
  const pastiglie = [];

  for (const reazione of REAZIONI) {
    const lista = Array.isArray(grezze[reazione.chiave]) ? grezze[reazione.chiave] : [];

    const mostrati = new Set(
      lista
        .map((voce) => (voce && voce.mostrato != null ? String(voce.mostrato).trim() : ""))
        .filter(Boolean)
    );

    if (mostrati.size === 0) continue;

    const mia =
      Boolean(mioId) &&
      Boolean(soggetto) &&
      lista.some(
        (voce) =>
          voce &&
          String(voce.reale) === String(mioId) &&
          String(voce.mostrato) === String(soggetto)
      );

    pastiglie.push({ ...reazione, conteggio: mostrati.size, mia });
  }

  return pastiglie;
}

// NIENTE SALTO IN FONDO PER LE SOLE REAZIONI. Vero quando la lista nuova ha
// gli STESSI messaggi, nello stesso ordine, e differisce solo nel campo
// `reazioni`. Nel dubbio (campi in ordine diverso, un'altra modifica insieme)
// risponde falso, e si torna al comportamento di sempre.
function soloReazioniCambiate(prima, dopo) {
  if (!Array.isArray(prima) || !Array.isArray(dopo)) return false;
  if (prima.length !== dopo.length || prima.length === 0) return false;

  for (let i = 0; i < dopo.length; i += 1) {
    if (prima[i].id !== dopo[i].id) return false;

    const { reazioni: reazioniPrima, ...restoPrima } = prima[i];
    const { reazioni: reazioniDopo, ...restoDopo } = dopo[i];

    if (JSON.stringify(restoPrima) !== JSON.stringify(restoDopo)) return false;
  }

  return true;
}

// Entro questa distanza dal fondo si è «già in fondo» (vedi lo scorrimento).
const SOGLIA_IN_FONDO_PX = 24;

// Lo scarto fra il wrap del messaggio e il menu «⋮» aperto: è il margine di
// `.lc__menu--sotto` / `.lc__menu--sopra` (--qpsc-space-4). Entra nella misura
// dello spazio che serve al menu.
const SCARTO_MENU_PX = 4;

// Il breakpoint del §14.2. Qui è una costante di JavaScript (non una media
// query) perché il LAYOUT a tre pannelli e le TRE SCHERMATE non sono lo stesso
// markup: sotto la soglia si mostra un pannello per volta.
const SOGLIA_COMPATTA = 768;

// -----------------------------------------------------------------------------
// LE COLONNE TRASCINABILI (cantiere LIVECHAT-DESK, Fetta 2)
// -----------------------------------------------------------------------------
// Sopra la soglia compatta, e solo su un computer col mouse, i due bordi fra
// i pannelli si trascinano: l'elenco (a sinistra) e il pannello partecipanti
// (a destra) cambiano larghezza, la conversazione al centro assorbe il resto.
// Doppio clic su una maniglia = larghezza predefinita di quella colonna.
//
// I PREDEFINITI sono le larghezze fisse del §14.2 di prima (232 e 216 px);
// i LIMITI valgono due volte, qui (clamp prima di scrivere) e nel foglio
// (`min-width`/`max-width` dentro `@media (min-width: 768px)`): i due numeri
// devono restare uguali.
const COLONNE_PREDEFINITE = { list: 232, info: 216 };
const LIMITI_COLONNE = {
  list: { min: 200, max: 480 },
  info: { min: 180, max: 360 }
};
// La custom property che il foglio legge per ciascuna colonna.
const VARIABILE_COLONNA = { list: "--lc-list-w", info: "--lc-info-w" };

function limitaColonna(colonna, px) {
  const { min, max } = LIMITI_COLONNE[colonna];
  return Math.min(max, Math.max(min, Math.round(px)));
}

// ⚠️ LA FORMA È QUELLA DI `CHIAVE_ORDINE_SQUADRE` (HomeCoach.jsx), non una
// nuova: costante esportata, `window.localStorage`, ogni accesso in
// `try/catch` con degrado silenzioso, valore riletto e VALIDATO prima
// dell'uso. Chiave globale per browser, sopravvive al logout. Dentro ci sono
// solo DUE NUMERI in px: nessun dato personale.
export const CHIAVE_LARGHEZZE_COLONNE = "qpscChatColWidths";

/** Sempre un oggetto valido: ogni voce assente, non numerica o fuori dai
 *  limiti ricade sul suo predefinito, una per una. */
function larghezzeSalvate() {
  try {
    const letto = JSON.parse(window.localStorage.getItem(CHIAVE_LARGHEZZE_COLONNE));
    if (!letto || typeof letto !== "object") return { ...COLONNE_PREDEFINITE };

    const valida = (colonna) => {
      const v = letto[colonna];
      const { min, max } = LIMITI_COLONNE[colonna];
      return typeof v === "number" && Number.isFinite(v) && v >= min && v <= max
        ? Math.round(v)
        : COLONNE_PREDEFINITE[colonna];
    };
    return { list: valida("list"), info: valida("info") };
  } catch (_) {
    return { ...COLONNE_PREDEFINITE };
  }
}

function salvaLarghezze(colonne) {
  try {
    window.localStorage.setItem(
      CHIAVE_LARGHEZZE_COLONNE,
      JSON.stringify({ list: colonne.list, info: colonne.info })
    );
  } catch (_) {
    /* localStorage non disponibile: si prosegue lo stesso. */
  }
}

// -----------------------------------------------------------------------------
// LA FORMA DEL VINCOLO D'ANNO (Via A, disegno del Blocco 3)
// -----------------------------------------------------------------------------
// La regola ammette una chat se è A-STAGIONALE (`anno: null` — diretta e
// annuncio) OPPURE se il suo `anno` è quello operativo. Non è una uguaglianza
// sola, e in una query di collezione le regole NON filtrano: devono essere i
// VINCOLI DELLA QUERY a garantire che ogni risultato sia consentito.
//
//   • VIA 2b (questa) — un `in` su [null, anno]: UNA query, UNA sottoscrizione.
//   • VIA 2a (ripiego) — due query, `anno == null` e `anno == operativo`, unite
//     in memoria.
//
// ⚠️ LA 2b È STATA RIFIUTATA DAVVERO, e l'8/9/2026 si è passati alla 2a.
// Il guasto non è stato silenzioso, come previsto qui: l'`onSnapshot` è finito
// nel ramo d'errore e la pagina l'ha detto. La causa, provata sull'emulatore:
// un `in` con più di un valore (o con un `null` dentro) lascia `anno`
// INDETERMINATO, la regola non è più dimostrabile dai vincoli e la query è
// negata per intero. Solo l'uguaglianza fissa l'anno — da cui le due query
// separate della 2a. Il resto del codice le reggeva già: la fusione per id e
// l'ordinamento avvengono in memoria.
const VIA_ANNO_CON_IN = false;

// «UNA CONVERSAZIONE È DAVVERO A SCHERMO?» — sede unica (D-05, L1 + L1-bis).
// Non basta `activeChat`: sul telefono il «Back» (`indietro`) riporta la vista
// all'elenco e LASCIA la chat impostata. Il metro vale due volte: l'indirizzo
// `?chat=` (che il service worker e `useFCMDeviceToken` leggono per tacere la
// notifica di quella chat) e le spunte di lettura (`POST /messages/read`).
function conversazioneASchermo(activeChat, vista, compatto) {
  return Boolean(activeChat) && (!compatto || vista !== "elenco");
}

export default function Messaging() {
  const { user, activeContext } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ===========================================================================
  // LE DUE IDENTITÀ, E QUALE SERVE A CHE COSA
  // ===========================================================================
  // • UID_LOGIN — l'uid del token Firebase. È l'identità che il RECINTO
  //   pretende: la regola confronta `request.auth.uid` con le voci di
  //   `lettori`. Sbagliarla non dà un errore: dà una LISTA VUOTA in silenzio.
  //   Si prende da `auth.currentUser`, la stessa fonte di `authHeaders`, cioè
  //   il login che ha davvero bussato.
  // • MIO_ID — il codice-documento della persona in `users`. È l'identità del
  //   MODELLO: `membri`, `nonLetti`, `lettoDa`, `autoreReale` parlano questa.
  //   Si risolve sulla rubrica, che è la stessa traduzione che il backend fa
  //   con `caricaUtenteChiamante`.
  const UID_LOGIN = String(
    (auth.currentUser && auth.currentUser.uid) || user?.firebaseUid || ""
  ).trim();

  // --- rubrica (anno operativo + persone) ------------------------------------
  const [anno, setAnno] = useState(null);
  const [persone, setPersone] = useState([]);
  const [rubricaStato, setRubricaStato] = useState("caricamento");
  const [rubricaErrore, setRubricaErrore] = useState("");

  // --- squadre, per la sola pastiglia categoria ------------------------------
  const [squadre, setSquadre] = useState([]);

  // --- elenco chat -----------------------------------------------------------
  const [chatList, setChatList] = useState([]);
  const [elencoStato, setElencoStato] = useState("attesa");
  const [elencoErrore, setElencoErrore] = useState("");
  const [filtroElenco, setFiltroElenco] = useState("");

  // --- conversazione ---------------------------------------------------------
  const [activeChat, setActiveChat] = useState(null);
  // La chat aperta che NON è nell'elenco: due casi distinti.
  //   • `chatFuoriElenco` — un documento che esiste ma non è arrivato dalla
  //     query (Via 2c: `getDoc` per id, sempre sicura perché la regola si
  //     valuta sul documento vero, non sui vincoli della query);
  //   • `chatPendente` — una conversazione che NON esiste ancora (una diretta
  //     appena scelta, il primo annuncio del club): non è un documento, è
  //     l'intenzione di scriverne uno.
  const [chatFuoriElenco, setChatFuoriElenco] = useState(null);
  const [chatPendente, setChatPendente] = useState(null);

  const [messages, setMessages] = useState([]);
  const [messaggiStato, setMessaggiStato] = useState("attesa");
  const [messaggiErrore, setMessaggiErrore] = useState("");

  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [editMessage, setEditMessage] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  // IL MENU «⋮» SOTTO O SOPRA (cantiere LIVECHAT-MENU): `"sotto"` sotto la
  // bolla e le sue pastiglie, `"sopra"` sopra la bolla. Lo decide la misura
  // all'apertura (vedi l'effetto «sotto o sopra»); `menuRef` è il riquadro
  // aperto — uno solo alla volta, voci o fila delle sei.
  const [versoMenu, setVersoMenu] = useState("sotto");
  const menuRef = useRef(null);

  // REAZIONI (Fetta 3b). `sceltaReazioneId`: il messaggio il cui menu «⋮» sta
  // mostrando la fila delle sei (dopo il tocco su «React»).
  // `elencoReazioni`: la finestra «chi ha reagito» — `null` chiusa, altrimenti
  // `{ messageId, stato: "caricamento" | "pronto" | "errore", reazioni }`.
  const [sceltaReazioneId, setSceltaReazioneId] = useState(null);
  const [elencoReazioni, setElencoReazioni] = useState(null);

  // Il soggetto a nome del quale si scrive nella chat aperta, quando le
  // possibilità sono più d'una (un genitore con DUE figli nello stesso gruppo).
  // Chiave: id della chat. Vuoto = «non ho ancora scelto», e allora vale la
  // prima possibilità.
  const [soggettoScelto, setSoggettoScelto] = useState({});

  // --- comandi admin ---------------------------------------------------------
  const [globalView, setGlobalView] = useState(false);
  const [filtroRuoli, setFiltroRuoli] = useState(false);

  // --- finestra «New chat» ---------------------------------------------------
  const [mostraSceltaPersona, setMostraSceltaPersona] = useState(false);
  const [filtroPersone, setFiltroPersone] = useState("");

  // ⚠️ QUI IL SOGGETTO SI SCEGLIE PRIMA, non dopo. L'id di una diretta è
  // deterministico e lo compongono il SOGGETTO MOSTRATO e il destinatario: per
  // un tutore con DUE figli la diretta con lo stesso coach è una conversazione
  // diversa a seconda del figlio. Sceglierlo dopo, nella barra di
  // composizione, arriverebbe a conversazione già aperta — cioè troppo tardi.
  const [soggettoNuovaChat, setSoggettoNuovaChat] = useState("");

  // --- finestra «Email» (solo admin, D6) -------------------------------------
  const [mostraEmail, setMostraEmail] = useState(false);
  const [emailPersone, setEmailPersone] = useState([]);
  const [emailStato, setEmailStato] = useState("attesa");
  const [emailErrore, setEmailErrore] = useState("");
  const [emailScelti, setEmailScelti] = useState([]);
  const [filtroEmail, setFiltroEmail] = useState("");

  // --- layout (§14.2) --------------------------------------------------------
  // Quale schermata si vede sotto la soglia compatta. Sopra la soglia i tre
  // pannelli convivono e questo valore governa solo il pannello info.
  const [vista, setVista] = useState("elenco");
  const [larghezza, setLarghezza] = useState(() =>
    typeof window === "undefined" ? SOGLIA_COMPATTA : window.innerWidth
  );

  const messagesContainerRef = useRef(null);

  // Per lo scorrimento (Fetta 3b): l'ultima lista di messaggi già disegnata,
  // con la sua chat, e se chi guarda sta in fondo alla conversazione.
  const ultimiMessaggiRef = useRef({ chat: null, messaggi: null });
  const inFondoRef = useRef(true);

  // LO SCORRITORE CHE NASCE È «IN FONDO» (cantiere LIVECHAT-MENU, caso «Back»).
  // In compatto il Back smonta la conversazione senza alcun evento `scroll`:
  // riaprendo la STESSA chat, `inFondoRef` resterebbe al falso lasciato da una
  // scorsa in alto, e la lista identica che la sottoscrizione riconsegna
  // passerebbe per «sole reazioni» — la chat si aprirebbe in cima. Uno
  // scorritore nuovo non ha posizione da rispettare: si scende come sempre.
  // E si scende SUBITO: nelle rimonte senza dati nuovi (in compatto
  // conversazione → info → conversazione, il passaggio della soglia dei 768px
  // quando da compatto si era sull'elenco o sulle info) la sottoscrizione
  // non riparte, l'effetto dello scorrimento non gira, e la conversazione
  // rinascerebbe in cima. Un elemento nuovo parte comunque da
  // `scrollTop = 0`: non c'è una posizione di lettura da perdere.
  // Stabile (`useCallback` senza dipendenze): React lo richiama solo quando
  // l'elemento nasce o muore, non a ogni resa.
  const agganciaScorritore = useCallback((elemento) => {
    messagesContainerRef.current = elemento;
    if (elemento) {
      elemento.scrollTop = elemento.scrollHeight;
      inFondoRef.current = true;
    }
  }, []);

  // ⚠️ CON UN ASCOLTATORE, non leggendo `window.innerWidth` dentro la resa: il
  // foglio precedente lo leggeva a mano in due punti e il layout non reagiva
  // al ridimensionamento finché la pagina non si ri-renderizzava per altro.
  useEffect(() => {
    const misura = () => setLarghezza(window.innerWidth);

    window.addEventListener("resize", misura);
    return () => window.removeEventListener("resize", misura);
  }, []);

  const compatto = larghezza < SOGLIA_COMPATTA;

  // La conversazione davvero a schermo (vedi `conversazioneASchermo`).
  const aSchermo = conversazioneASchermo(activeChat, vista, compatto);

  // --- colonne trascinabili (LIVECHAT-DESK, Fetta 2) -------------------------
  // La larghezza di elenco e partecipanti arriva al foglio come custom
  // property su `.lc__body`; il foglio la usa SOLO da 768px in su, quindi sul
  // telefono questo stato non ha effetto.
  //
  // ⚠️ LA FORMA DEL GESTO È QUELLA DI `CampoPartita.jsx`: `onPointerDown` sulla
  // maniglia apre il gesto (con la cattura del puntatore), gli ascoltatori su
  // `window` vivono quanto il gesto e si smontano al rilascio. Durante il
  // trascinamento la variabile si scrive DIRETTAMENTE sull'elemento (niente
  // ri-resa dell'intera pagina a ogni pixel); lo stato React e il valore
  // persistito si aggiornano una volta sola, al rilascio.
  const [colonne, setColonne] = useState(larghezzeSalvate);
  const [trascinamento, setTrascinamento] = useState(null);
  const corpoRef = useRef(null);
  const larghezzaInCorsoRef = useRef(null);

  const scriviVariabile = useCallback((colonna, px) => {
    if (corpoRef.current) {
      corpoRef.current.style.setProperty(VARIABILE_COLONNA[colonna], `${px}px`);
    }
  }, []);

  const inizioTrascinamento = useCallback(
    (e, colonna) => {
      if (e.button !== 0) return;
      e.preventDefault();
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // Un browser senza cattura: i listener su window bastano.
      }
      larghezzaInCorsoRef.current = colonne[colonna];
      setTrascinamento({
        colonna,
        pointerId: e.pointerId,
        x0: e.clientX,
        w0: colonne[colonna]
      });
    },
    [colonne]
  );

  const movimentoTrascinamento = useCallback(
    (e) => {
      const t = trascinamento;
      if (!t || t.pointerId !== e.pointerId) return;
      const dx = e.clientX - t.x0;
      // Il bordo dell'elenco sta alla sua DESTRA, quello dei partecipanti
      // alla loro SINISTRA: lo stesso gesto verso destra allarga il primo e
      // stringe il secondo.
      const px = limitaColonna(t.colonna, t.colonna === "list" ? t.w0 + dx : t.w0 - dx);
      larghezzaInCorsoRef.current = px;
      scriviVariabile(t.colonna, px);
    },
    [trascinamento, scriviVariabile]
  );

  const fineTrascinamento = useCallback(
    (e) => {
      const t = trascinamento;
      if (!t || t.pointerId !== e.pointerId) return;
      setTrascinamento(null);
      const px = larghezzaInCorsoRef.current;
      if (px === colonne[t.colonna]) return;
      const nuove = { ...colonne, [t.colonna]: px };
      setColonne(nuove);
      salvaLarghezze(nuove);
    },
    [trascinamento, colonne]
  );

  const annullaTrascinamento = useCallback(
    (e) => {
      const t = trascinamento;
      if (!t || t.pointerId !== e.pointerId) return;
      setTrascinamento(null);
      // Lo stato non è cambiato, quindi React non riscrive la variabile:
      // la si riporta a mano al valore d'inizio gesto.
      scriviVariabile(t.colonna, t.w0);
    },
    [trascinamento, scriviVariabile]
  );

  const trascinamentoAperto = Boolean(trascinamento);
  useEffect(() => {
    if (!trascinamentoAperto) return undefined;

    window.addEventListener("pointermove", movimentoTrascinamento);
    window.addEventListener("pointerup", fineTrascinamento);
    window.addEventListener("pointercancel", annullaTrascinamento);
    return () => {
      window.removeEventListener("pointermove", movimentoTrascinamento);
      window.removeEventListener("pointerup", fineTrascinamento);
      window.removeEventListener("pointercancel", annullaTrascinamento);
    };
  }, [trascinamentoAperto, movimentoTrascinamento, fineTrascinamento, annullaTrascinamento]);

  // Doppio clic sulla maniglia: la colonna torna al predefinito, e così il
  // valore persistito.
  const ripristinaColonna = useCallback(
    (colonna) => {
      const px = COLONNE_PREDEFINITE[colonna];
      scriviVariabile(colonna, px);
      const nuove = { ...colonne, [colonna]: px };
      setColonne(nuove);
      salvaLarghezze(nuove);
    },
    [colonne, scriviVariabile]
  );

  // ===========================================================================
  // IL RUOLO DI CHI GUARDA — dal gate unico, mai dalla grafia
  // ===========================================================================
  const eAmministratore =
    canAccess(user, "ADMIN") || canAccess(user, "SUPERADMIN");

  // ===========================================================================
  // IL CAPPELLO CHE SI PORTA ADESSO — filtro di VISTA, mai un permesso
  // ===========================================================================
  // ⚠️ CONFINE DI SICUREZZA, non negoziabile. Da qui in giù la pagina si
  // regola sul CAPPELLO e non più sul permesso, ma SOLO per decidere che cosa
  // lo schermo mostra e offre. I permessi restano dove sono sempre stati: il
  // backend li ricava dal token a ogni richiesta, e un amministratore resta
  // amministratore lato server qualunque cappello indossi. Il cappello NON
  // viene inviato al backend, in nessuna forma: il corpo di `POST /messages`
  // è `{ tipo, destinatario, soggettoDichiarato, testo, rispostaA }` e non
  // porta contesto. (Il vecchio `senderContext` è codice morto: non si
  // resuscita.)
  //
  // ⚠️ `eAmministratore` NON sparisce e non va sostituito ovunque: resta il
  // metro di ciò che si POSSIEDE. Il cappello dice che cosa si sta FACENDO.
  const cappelloAdmin = isActingAs(user, activeContext, "ADMIN", "SUPERADMIN");
  const cappelloGenitore = isActingAs(user, activeContext, "PARENT");
  const cappelloAdulto = isActingAs(user, activeContext, "COACH", "PLAYER");

  // LA VISTA AMMINISTRATIVA vuole TUTTE E DUE le cose: il permesso di esserlo e
  // il cappello di starlo facendo. Le due condizioni oggi coincidono — il
  // cappello viene sempre da un ruolo posseduto — e proprio per questo tenerle
  // entrambe non costa nulla: se un domani un cappello arrivasse da altrove,
  // qui non si aprirebbe comunque nulla che il permesso non consenta già.
  const vistaAmministrativa = eAmministratore && cappelloAdmin;

  // Le squadre del cappello, dalla sede unica (regola B). Servono a filtrare i
  // gruppi di squadra: con la tessera di COACH della sola U10, il cappello
  // COACH mostra la U10 e non la U12 di cui si è giocatori.
  //
  // ⚠️ DUE ESENZIONI, ed entrambe sono obbligatorie, non comodità:
  //
  //  1. IL CAPPELLO AMMINISTRATIVO NON FILTRA. Quello di ADMIN/SUPERADMIN è
  //     SINTETICO (`HomeContext.chooseRole`: «il ruolo scelto in grafia
  //     canonica, e NESSUNA squadra») perché un admin non ha squadre proprie:
  //     vede il club perché è admin. Filtrare per le sue squadre gli
  //     svuoterebbe la barra.
  //
  //  2. UN CAPPELLO CHE NON NOMINA SQUADRE NON FILTRA NULLA. La tessera di
  //     PARENT non porta squadre — le squadre le hanno i FIGLI — e nemmeno un
  //     cappello sintetico ne porta. Applicare l'elenco vuoto come se fosse un
  //     filtro cancellerebbe «Your children's teams», cioè esattamente la
  //     sezione che il cappello PARENT deve mostrare. «Nessuna squadra
  //     nominata» significa «niente da filtrare», non «nessuna squadra».
  //     Chi restringe la vista del genitore è l'incrocio coi figli, più sotto.
  const squadreCappello = useMemo(() => {
    if (vistaAmministrativa) return null; // esenzione 1: nessun filtro

    const { teams, allTeams } = squadreDellaTessera(activeContext);
    if (allTeams) return null;

    const nomi = teams
      .map((voce) => normalizza(voce))
      .filter((voce) => voce !== "");

    if (nomi.length === 0) return null; // esenzione 2: nessun filtro

    return new Set(nomi);
  }, [vistaAmministrativa, activeContext]);

  // ===========================================================================
  // 1) LA RUBRICA, PRIMA DI TUTTO
  // ===========================================================================
  // Porta due cose che dal browser non si possono avere altrimenti: l'ANNO
  // OPERATIVO (la cassettina `clubConfig` ricade nel default-deny delle regole)
  // e le PERSONE, già filtrate per competenza dal backend — un socio ordinario
  // riceve solo chi condivide una chat con lui, non l'anagrafica intera.
  //
  // ⚠️ Sostituisce `GET /users`, che consegnava a OGNI utente autenticato —
  // anche a un minore — l'elenco delle persone ACTIVE.
  //
  // ⚠️ Un 409 («Could not read the club season…») significa NIENTE ANNO, quindi
  // niente elenco: si mostra il motivo, non un caricamento perpetuo.
  useEffect(() => {
    let vivo = true;

    setRubricaStato("caricamento");
    setRubricaErrore("");

    api
      .get("/chat/directory")
      .then((dati) => {
        if (!vivo) return;

        setAnno(typeof dati?.anno === "number" ? dati.anno : null);
        setPersone(Array.isArray(dati?.persone) ? dati.persone : []);
        setRubricaStato("pronta");
      })
      .catch((err) => {
        if (!vivo) return;

        let motivo = "";

        try {
          motivo = JSON.parse(err?.body || "{}").error || "";
        } catch {
          motivo = "";
        }

        setRubricaErrore(motivo || "Chat is not available right now.");
        setRubricaStato("errore");
      });

    return () => {
      vivo = false;
    };
  }, []);

  // LE SQUADRE, per la SOLA pastiglia categoria (§2). La categoria non sta
  // sull'utente: sta sulla SQUADRA, e la mappa è per NOME perché è il nome che
  // il documento-chat porta in `teamName`. Stesso calco della home del
  // genitore. Se la chiamata non riesce, la pastiglia tace e basta.
  useEffect(() => {
    let vivo = true;

    api
      .get("/teams")
      .then((dati) => {
        if (vivo) setSquadre(Array.isArray(dati) ? dati : []);
      })
      .catch(() => {
        /* nessuna categoria: la pastiglia tace */
      });

    return () => {
      vivo = false;
    };
  }, []);

  const categoriaDi = useMemo(() => {
    const perNome = new Map(
      squadre
        .filter((squadra) => squadra && typeof squadra === "object" && squadra.name)
        .map((squadra) => [squadra.name, normalizza(squadra.category)])
    );

    return (nomeSquadra) => perNome.get(nomeSquadra) || "";
  }, [squadre]);

  // La mappa delle persone: si costruisce UNA VOLTA e risponde a tutti gli
  // alias storici dell'identificativo (id, uid, firebaseUid), perché i
  // documenti già scritti non usano tutti la stessa chiave.
  const perId = useMemo(() => {
    const mappa = new Map();

    for (const persona of persone) {
      for (const chiave of [persona.id, persona.uid, persona.firebaseUid]) {
        const valore = String(chiave || "").trim();
        if (valore && !mappa.has(valore)) mappa.set(valore, persona);
      }
    }

    return mappa;
  }, [persone]);

  // Il codice-documento di chi guarda: si chiede alla rubrica, che è la stessa
  // traduzione del backend. Ripiego sul documento utente di AuthContext.
  const MIO_ID = useMemo(() => {
    const dallaRubrica = perId.get(UID_LOGIN);
    if (dallaRubrica) return String(dallaRubrica.id);

    return String((user?.id || user?.uid || user?.firebaseUid) || "").trim();
  }, [perId, UID_LOGIN, user]);

  // I miei figli, per id. Suggerimento di schermo, non permesso: vedi la nota
  // all'import di `idsDeiFigli`.
  const idsFigli = useMemo(() => idsDeiFigli(user), [user]);

  // ===========================================================================
  // IL RISOLUTORE DELLE PERSONE — sede unica dentro la pagina
  // ===========================================================================
  // Un id → nome, etichetta di ruolo, foto. Nient'altro: la rubrica non porta
  // email né data di nascita (`proiezionePerLaChat` lo dichiara e lo fa), e
  // quindi un recapito di un minore non è a schermo per COSTRUZIONE, non per un
  // filtro dello schermo.
  const risolviPersona = useCallback(
    (id) => {
      const chiave = String(id || "").trim();
      const persona = chiave ? perId.get(chiave) : null;

      if (!persona) {
        return {
          id: chiave,
          nome: PERSONA_SCONOSCIUTA,
          etichettaRuolo: "",
          iniziale: PERSONA_SCONOSCIUTA.charAt(0),
          photoUrl: "",
          photoUserId: "",
          nota: false
        };
      }

      const nome = `${persona.firstName || ""} ${persona.lastName || ""}`.trim();

      return {
        id: String(persona.id),
        nome: nome || PERSONA_SCONOSCIUTA,
        // ⚠️ MAI la sigla tecnica maiuscola: l'etichetta leggibile, dal gate
        // unico (doc 07 §14.5 e §3.2).
        etichettaRuolo: roleLabel(persona.role),
        iniziale: (nome || PERSONA_SCONOSCIUTA).charAt(0).toUpperCase(),
        photoUrl: persona.photoUrl || "",
        photoUserId: String(persona.id),
        nota: true
      };
    },
    [perId]
  );

  // L'ETICHETTA DI UNA VOCE DEL SELETTORE — sede unica, la usano tutti e due i
  // selettori (quello del compositore e quello di «New chat»).
  //
  // ⚠️ PUNTO 7: SI LEGGE IL NOME, NON «yourself». Un genitore che scrive nella
  // squadra di Noah deve leggere «as Noah», e negli U10 «as Amelia»: la persona
  // per conto della quale si parla ha un nome, e quel nome è l'unica cosa che
  // dice davvero come sarà firmato il messaggio. «as yourself» resta soltanto
  // come RIPIEGO per la propria voce quando la rubrica non sa dire il nome
  // proprio — meglio «yourself» che l'anonimo «Club member», che qui sarebbe
  // una regressione.
  //
  // ⚠️ Per un FIGLIO la rubrica può davvero non saperlo, e si legge «as Club
  // member»: la rubrica è filtrata per competenza e un figlio senza squadra né
  // dirette non vi compare (lo stesso per un archiviato). È una situazione di
  // DATI, accettata dal committente: non la si maschera qui.
  const etichettaSoggetto = useCallback(
    (id) => {
      const persona = risolviPersona(id);

      if (!persona.nota && String(id) === MIO_ID) return "as yourself";

      return `as ${persona.nome}`;
    },
    [risolviPersona, MIO_ID]
  );

  // ===========================================================================
  // 2) L'ELENCO — UGUALE PER OGNI RUOLO
  // ===========================================================================
  // `where("lettori","array-contains", <uid del LOGIN>)` più il vincolo d'anno.
  // Non c'è più un ramo per ruolo: le liste `lettori` le ha già composte il
  // backend pesando ruolo, tessere e anno. La «Global View» dell'admin non è
  // più una query diversa (un ADMIN è nei `lettori` di tutti i gruppi): è un
  // filtro di PRESENTAZIONE, più sotto.
  //
  // ⚠️ NESSUN `orderBy` NELLA QUERY. Un `orderBy("ultimoQuando")` ESCLUDEREBBE
  // le chat che quel campo non ce l'hanno — cioè i gruppi mai scritti, che
  // nascono senza meta d'anteprima: una squadra appena creata resterebbe
  // invisibile finché qualcuno non vi parla. Si ordina in memoria (sono pochi
  // documenti) e in cambio l'indice composto da creare a mano si semplifica:
  // `lettori` (array-contains) + `anno` (asc), senza campo d'ordinamento.
  useEffect(() => {
    if (rubricaStato !== "pronta") return undefined;
    if (anno === null || !UID_LOGIN) return undefined;

    setElencoStato("caricamento");
    setElencoErrore("");

    const vincoliAnno = VIA_ANNO_CON_IN
      ? [[where("anno", "in", [null, anno])]]
      : [[where("anno", "==", null)], [where("anno", "==", anno)]];

    // Una fetta di risultati per sottoscrizione: si fondono per id, così la
    // Via 2a (due query) e la Via 2b (una) producono lo stesso elenco.
    const fette = vincoliAnno.map(() => []);

    const componi = () => {
      const perChat = new Map();

      for (const fetta of fette) {
        for (const chat of fetta) perChat.set(chat.id, chat);
      }

      const elenco = [...perChat.values()].sort((a, b) => {
        const aQuando = a.ultimoQuando?.seconds || 0;
        const bQuando = b.ultimoQuando?.seconds || 0;
        return bQuando - aQuando;
      });

      setChatList(elenco);
      setElencoStato("pronto");
    };

    const disiscrizioni = vincoliAnno.map((vincoli, indice) =>
      onSnapshot(
        query(collection(db, "chats"), where("lettori", "array-contains", UID_LOGIN), ...vincoli),
        (snapshot) => {
          fette[indice] = snapshot.docs.map((documento) => ({
            id: documento.id,
            ...documento.data()
          }));
          componi();
        },
        (err) => {
          // ⚠️ QUI SI VEDE SE UNA QUERY NON PASSA, e si vede subito: la query
          // fallisce per intero. `failed-precondition` di solito significa
          // INDICE MANCANTE (il messaggio porta un link di creazione già
          // compilato): si guarda prima l'indice, poi la regola.
          console.error("Chat list error:", err?.code || err);
          setElencoErrore("Chats could not be loaded.");
          setElencoStato("errore");
        }
      )
    );

    return () => {
      for (const disiscrivi of disiscrizioni) disiscrivi();
    };
  }, [rubricaStato, anno, UID_LOGIN]);

  // Cambiando cappello si riparte puliti: l'elenco e la conversazione aperta
  // appartengono al contesto con cui li si è guardati.
  useEffect(() => {
    setActiveChat(null);
    setChatFuoriElenco(null);
    setChatPendente(null);
    setMessages([]);
    setVista("elenco");
  }, [activeContext]);

  // ===========================================================================
  // 3) LA CHAT APERTA CHE NON È NELL'ELENCO — Via 2c, `getDoc` per id
  // ===========================================================================
  // Il vincolo di query non c'entra: la regola si valuta sul documento vero.
  // Sostituisce il ramo `db.collection("chats").doc(...)` — sintassi
  // *namespaced* su un'istanza MODULARE, che lanciava `TypeError` ogni volta
  // che si accendeva.
  useEffect(() => {
    if (!activeChat) {
      setChatFuoriElenco(null);
      return undefined;
    }

    if (chatList.some((chat) => chat.id === activeChat)) {
      setChatFuoriElenco(null);
      return undefined;
    }

    let vivo = true;

    getDoc(doc(db, "chats", activeChat))
      .then((istantanea) => {
        if (!vivo) return;
        // `exists` è un METODO nell'SDK modulare, non una proprietà.
        setChatFuoriElenco(
          istantanea.exists() ? { id: istantanea.id, ...istantanea.data() } : null
        );
      })
      .catch((err) => {
        if (!vivo) return;
        console.warn("Chat not readable:", err?.code || err);
        setChatFuoriElenco(null);
      });

    return () => {
      vivo = false;
    };
  }, [activeChat, chatList]);

  // La chat aperta, da dove che venga.
  const chatCorrente = useMemo(() => {
    if (!activeChat) return null;

    return (
      chatList.find((chat) => chat.id === activeChat) ||
      (chatFuoriElenco && chatFuoriElenco.id === activeChat ? chatFuoriElenco : null) ||
      (chatPendente && chatPendente.id === activeChat ? chatPendente : null)
    );
  }, [activeChat, chatList, chatFuoriElenco, chatPendente]);

  // ⚠️ UNA CONVERSAZIONE CHE ANCORA NON ESISTE NON SI INTERROGA. Il recinto
  // dei messaggi è tutto sul PADRE (`chatPadreLeggibile` fa `exists()` sul
  // documento-chat): con un padre inesistente la query dei messaggi viene
  // NEGATA, non risponde «nessun messaggio». Senza questa guardia, aprire una
  // diretta nuova o il primo annuncio del club mostrerebbe un errore al posto
  // dello stato vuoto — e l'errore non sarebbe nemmeno vero.
  const conversazioneNuova = Boolean(
    activeChat &&
      chatPendente &&
      chatPendente.id === activeChat &&
      !chatList.some((chat) => chat.id === activeChat) &&
      !(chatFuoriElenco && chatFuoriElenco.id === activeChat)
  );

  // ===========================================================================
  // 4) I MESSAGGI — `orderBy("quando")`
  // ===========================================================================
  // Il recinto regge la forma della query (il vincolo è tutto sul PADRE:
  // `chatPadreLeggibile`), ma il campo d'ordinamento no: il backend scrive
  // `quando` e non scrive mai `timestamp`.
  useEffect(() => {
    if (!activeChat) {
      setMessages([]);
      setMessaggiStato("attesa");
      return undefined;
    }

    if (conversazioneNuova) {
      setMessages([]);
      setMessaggiStato("pronti");
      return undefined;
    }

    setMessaggiStato("caricamento");
    setMessaggiErrore("");

    const q = query(
      collection(db, "chats", activeChat, "messages"),
      orderBy("quando", "asc")
    );

    const disiscrivi = onSnapshot(
      q,
      async (snapshot) => {
        const documenti = snapshot.docs;

        setMessages(
          documenti.map((documento) => ({ id: documento.id, ...documento.data() }))
        );
        setMessaggiStato("pronti");

        // LA SPUNTA È DELLA PERSONA CHE HA FATTO L'ACCESSO — quella che il
        // backend ricava dal token — non del soggetto mostrato: `lettoDa`
        // porta il CODICE-DOCUMENTO (`String(chiamante.id)`).
        // ⚠️ SOLO SE LA CONVERSAZIONE È DAVVERO A SCHERMO (D-05, L1-bis): sul
        // telefono, dopo il «Back», la chat resta impostata ma si guarda
        // l'elenco — e non si segna letto ciò che non si sta vedendo. Tornando
        // sulla conversazione `aSchermo` cambia, la sottoscrizione riparte e
        // la prima istantanea segna i messaggi rimasti indietro.
        const daSegnare = !aSchermo ? [] : documenti.filter((documento) => {
          const dati = documento.data();
          return (
            String(dati.autoreReale) !== MIO_ID &&
            !(Array.isArray(dati.lettoDa) ? dati.lettoDa : []).includes(MIO_ID)
          );
        });

        if (daSegnare.length > 0) {
          await Promise.all(
            daSegnare.map((documento) =>
              api
                .post("/messages/read", { chatId: activeChat, messageId: documento.id })
                // Un segna-letto rifiutato non deve rompere la lettura della
                // conversazione: si registra e si prosegue.
                .catch((err) => console.warn("Read update refused:", err?.status || err))
            )
          );
        }
      },
      (err) => {
        console.error("Messages error:", err?.code || err);
        setMessaggiErrore("Messages could not be loaded.");
        setMessaggiStato("errore");
      }
    );

    return () => disiscrivi();
  }, [activeChat, MIO_ID, conversazioneNuova, aSchermo]);

  // ===========================================================================
  // 5) IL PALLINO — lo azzera il BACKEND
  // ===========================================================================
  // Il client non scrive più su `chats`: `setDoc({ unreadCounters: … })`
  // toccava un campo che nessuno scrive più e che il recinto nega comunque.
  // La rotta azzera la SOLA chiave del chiamante, e la chiave è il
  // codice-documento — la stessa che il conteggio usa.
  useEffect(() => {
    // Su una conversazione che ancora non esiste non c'è nulla da azzerare: la
    // rotta risponderebbe 404.
    if (!activeChat || conversazioneNuova) return;

    api
      .post("/chat/letta", { chatId: activeChat })
      .catch((err) => console.warn("Reset unread refused:", err?.status || err));
  }, [activeChat, conversazioneNuova]);

  // Lo scorrimento in fondo, a ogni messaggio nuovo.
  // ⚠️ NON PER LE SOLE REAZIONI (Fetta 3b): una reazione riscrive il documento
  // del messaggio e `onSnapshot` consegna una lista nuova — senza questo
  // controllo chi reagisce a un messaggio vecchio (o riceve una reazione
  // altrui) verrebbe riportato in fondo. Se la chat è la stessa e sono
  // cambiate solo le `reazioni`, si scende SOLO se si era già in fondo (così la
  // pastiglia comparsa sotto l'ultima bolla resta in vista). Messaggio nuovo,
  // chat diversa, o qualunque altra modifica: comportamento di sempre.
  useEffect(() => {
    const contenitore = messagesContainerRef.current;
    const prima = ultimiMessaggiRef.current;

    ultimiMessaggiRef.current = { chat: activeChat, messaggi: messages };

    if (
      prima.chat === activeChat &&
      soloReazioniCambiate(prima.messaggi, messages) &&
      !inFondoRef.current
    ) {
      return;
    }

    if (contenitore) {
      contenitore.scrollTop = contenitore.scrollHeight;
      // Dopo l'assegnazione si è in fondo per definizione. Se il contenuto
      // non sborda nessun evento `scroll` parte, e senza questa riga resterebbe
      // il valore lasciato dalla chat di prima (R1).
      inFondoRef.current = true;
    }
  }, [messages, activeChat]);

  // IL MENU «⋮» SOTTO O SOPRA (cantiere LIVECHAT-MENU). All'apertura, prima
  // che il browser disegni (`useLayoutEffect`: niente lampo nel verso
  // sbagliato), si misura il riquadro VERO — 1-4 voci — e lo spazio che resta
  // fra il wrap del messaggio e i bordi visibili dello scorritore. Sotto se ci
  // sta (il caso normale, la bolla resta scoperta); altrimenti sopra se ci sta
  // (l'ultimo messaggio: scorrere per raggiungere il menu lo chiuderebbe);
  // se non ci sta da nessuna parte, il lato con più spazio. La prima bolla in
  // cima è il caso simmetrico: sopra non c'è posto, si apre sotto.
  // Si decide UNA volta, sulle voci: la fila delle sei che le sostituisce
  // dopo «React» (~50px) è più bassa di Reply + React, quindi sta dove
  // stavano le voci e il riquadro non salta dall'altra parte.
  useLayoutEffect(() => {
    const menu = menuRef.current;
    const contenitore = messagesContainerRef.current;
    const wrap = menu?.parentElement;

    if (!openMenuId || !menu || !contenitore || !wrap) return;

    const altezza = menu.offsetHeight + SCARTO_MENU_PX;
    const rWrap = wrap.getBoundingClientRect();
    const rScorritore = contenitore.getBoundingClientRect();
    const spazioSotto = rScorritore.bottom - rWrap.bottom;
    const spazioSopra = rWrap.top - rScorritore.top;

    let verso;
    if (spazioSotto >= altezza) verso = "sotto";
    else if (spazioSopra >= altezza) verso = "sopra";
    else verso = spazioSotto >= spazioSopra ? "sotto" : "sopra";

    setVersoMenu(verso);
  }, [openMenuId]);

  // Cambiando conversazione, la fila delle sei e l'elenco dei nomi di quella
  // di prima non hanno più senso.
  useEffect(() => {
    setSceltaReazioneId(null);
    setElencoReazioni(null);
  }, [activeChat]);

  // La chiusura del menu contestuale al clic fuori.
  useEffect(() => {
    const fuori = (e) => {
      if (!e.target.closest(".lc__menu") && !e.target.closest(".lc__menu-btn")) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", fuori);
    return () => document.removeEventListener("mousedown", fuori);
  }, []);

  // ===========================================================================
  // A NOME DI CHI SCRIVO — dal LEGAME, mai da `localStorage`
  // ===========================================================================
  // Le possibilità in una conversazione sono, insieme:
  //   • me stesso, se sono `membro` di quella chat (un giocatore adulto nella
  //     propria squadra, un amministratore ovunque);
  //   • i miei figli che sono `membri` di quella chat.
  // Una sola possibilità → automatica, nessuna domanda. Due o più → selettore
  // «as ⟨nome⟩» nella barra di composizione (è il caso di frontiera: un
  // genitore con due figli nella stessa squadra). Nessuna → non ho titolo a
  // scrivervi e la barra resta spenta: il backend risponderebbe 403.
  //
  // ⚠️ Per una conversazione che ANCORA NON ESISTE (una diretta appena scelta)
  // non ci sono `membri` da incrociare: chi ha figli si firma con un figlio
  // (D1: «il PARENT non scrive MAI a proprio nome, in nessun contesto»), chi
  // non ne ha si firma da sé.
  // ⚠️ ANCHE QUI IL TIMONE È IL CAPPELLO, non il permesso — e per due ragioni
  // distinte, che conviene tenere separate:
  //
  //  PUNTO 4 — SOTTO IL CAPPELLO ADMIN SI SCRIVE OVUNQUE, FIRMANDO COL PROPRIO
  //  NOME. Il backend GIÀ lo consente in tutti e quattro i rami di
  //  `POST /messages` (l'unica nozione di amministratore è `eAmministratore`,
  //  calcolata dal token, e nessun ramo la nega a un SUPERADMIN). A negarlo era
  //  soltanto questo `useMemo`, che chiedeva l'appartenenza ai `membri` là dove
  //  il modello mette l'amministratore nei soli `lettori`: un compositore
  //  spento per effetto collaterale, non per difesa. Si toglie l'effetto
  //  collaterale, non una difesa: la difesa sta nel token, e resta.
  //
  //  PUNTO 7 — SOTTO IL CAPPELLO PARENT SI FIRMA COI FIGLI, MAI DA SÉ. Perché
  //  oggi in un gruppo di squadra di un figlio comparisse «as yourself» c'è una
  //  ragione precisa, e non è l'etichetta: `MIO_ID` finiva DAVVERO fra i
  //  soggetti possibili, perché il genitore era `membro` di quel gruppo. Lo si
  //  è per due vie, entrambe legittime — avere una tessera propria in quella
  //  squadra (genitore-allenatore, genitore-giocatore), oppure essere ADMIN,
  //  che la fondazione iscrive fra i `membri` di OGNI gruppo
  //  (`if (amministratore.admin) voce.membri.add(amministratore.id)`). Il
  //  cappello scioglie il nodo senza toccare né i `membri` né i `lettori`: con
  //  quale veste sto scrivendo lo dice la persona, non l'anagrafica.
  const soggettiPossibili = useMemo(() => {
    if (!chatCorrente) return [];

    // I due canali del club li scrive un amministratore A NOME PROPRIO: il
    // backend RIFIUTA un soggetto dichiarato su annuncio e ruolo.
    if (chatCorrente.tipo === TIPO_ANNUNCIO || chatCorrente.tipo === TIPO_RUOLO) {
      return vistaAmministrativa && MIO_ID ? [MIO_ID] : [];
    }

    // PUNTO 4: col cappello amministrativo il compositore c'è OVUNQUE — gruppi
    // di squadra e dirette fra terzi compresi — e offre una voce sola, la
    // propria. Non si firma mai a nome di un figlio mentre si amministra.
    if (vistaAmministrativa) return MIO_ID ? [MIO_ID] : [];

    const membri = Array.isArray(chatCorrente.membri)
      ? chatCorrente.membri.map(String)
      : null;

    if (membri === null) {
      // Conversazione non ancora nata: non c'è nulla da incrociare.
      if (cappelloAdulto) return MIO_ID ? [MIO_ID] : [];

      return idsFigli.length > 0 ? idsFigli.map(String) : MIO_ID ? [MIO_ID] : [];
    }

    const possibili = [];

    // Col cappello PARENT me stesso NON è una possibilità, nemmeno dove sono
    // membro: è la lettura fedele di D1 («il PARENT non scrive MAI a proprio
    // nome»), che finora saltava proprio nel caso del genitore-allenatore.
    if (MIO_ID && membri.includes(MIO_ID) && !cappelloGenitore) {
      possibili.push(MIO_ID);
    }

    // Col cappello COACH/PLAYER i figli non sono una possibilità: in quella
    // veste si parla da adulti, e la firma è la propria.
    if (!cappelloAdulto) {
      for (const idFiglio of idsFigli) {
        if (membri.includes(String(idFiglio)) && !possibili.includes(String(idFiglio))) {
          possibili.push(String(idFiglio));
        }
      }
    }

    return possibili;
  }, [
    chatCorrente,
    idsFigli,
    MIO_ID,
    vistaAmministrativa,
    cappelloGenitore,
    cappelloAdulto
  ]);

  // I soggetti possibili per una conversazione che ancora non esiste. Vale la
  // stessa regola del cappello: chi amministra o parla da adulto si firma da
  // sé, il genitore si firma coi figli (D1).
  //
  // ⚠️ Qui il soggetto si sceglie PRIMA, e non è una finezza: l'id di una
  // diretta lo compongono il soggetto mostrato e il destinatario, quindi la
  // scelta cambia QUALE conversazione si apre, non solo come la si firma.
  const soggettiPerNuovaChat = useMemo(() => {
    if (vistaAmministrativa || cappelloAdulto) return MIO_ID ? [MIO_ID] : [];

    return idsFigli.length > 0 ? idsFigli.map(String) : MIO_ID ? [MIO_ID] : [];
  }, [idsFigli, MIO_ID, vistaAmministrativa, cappelloAdulto]);

  const soggettoCorrente = useMemo(() => {
    if (soggettiPossibili.length === 0) return "";

    const scelto = activeChat ? soggettoScelto[activeChat] : "";

    return soggettiPossibili.includes(scelto) ? scelto : soggettiPossibili[0];
  }, [soggettiPossibili, soggettoScelto, activeChat]);

  // ===========================================================================
  // LE REAZIONI — dove si disegnano, e chi può reagire (Fetta 3b)
  // ===========================================================================
  // Solo gruppi di squadra e dirette: su annuncio e ruolo il backend le nega
  // sempre («Reactions are not available in this chat»), e lì non compaiono
  // né le pastiglie né «React».
  const reazioniNellaChat =
    chatCorrente?.tipo === TIPO_SQUADRA || chatCorrente?.tipo === TIPO_DIRETTA;

  // «React» si offre solo dove il server la accetterebbe:
  //  • c'è un soggetto con cui firmare (`soggettiPossibili`, la stessa regola
  //    del compositore — D1 compresa: col cappello PARENT il soggetto è sempre
  //    un figlio, mai se stessi; il recinto vero è nel server, Fetta 3a);
  //  • in DIRETTA il soggetto è fra i `membri`: l'amministratore che in Global
  //    View guarda una diretta fra terzi ha il compositore, ma una reazione
  //    lì sarebbe un 403 garantito — quindi niente «React».
  const puoReagire = useMemo(() => {
    if (!reazioniNellaChat || conversazioneNuova) return false;
    if (soggettiPossibili.length === 0 || !soggettoCorrente) return false;

    if (chatCorrente.tipo === TIPO_DIRETTA) {
      const membri = Array.isArray(chatCorrente.membri)
        ? chatCorrente.membri.map(String)
        : [];

      return membri.includes(String(soggettoCorrente));
    }

    return true;
  }, [
    reazioniNellaChat,
    conversazioneNuova,
    soggettiPossibili,
    soggettoCorrente,
    chatCorrente
  ]);

  // ===========================================================================
  // L'INVIO
  // ===========================================================================
  // Il corpo si compone dai CAMPI del documento-chat, mai dall'id: l'id è un
  // fossile e dopo una rinomina porta ancora il nome vecchio (è il difetto S5,
  // che il backend ha già eliminato e che qui non si ripete).
  const inviaMessaggio = async () => {
    if (!activeChat || !chatCorrente) return;
    if (!input.trim()) return;
    if (sending) return;

    setSending(true);

    try {
      setSendError("");

      if (editMessage) {
        await api.post("/messages/edit", {
          chatId: activeChat,
          messageId: editMessage.id,
          testo: input
        });

        setEditMessage(null);
        setInput("");
        return;
      }

      const tipo = chatCorrente.tipo;

      let destinatario = null;

      if (tipo === TIPO_SQUADRA) {
        // ⚠️ IL NOME DELLA SQUADRA È `teamName`, il campo che la rinomina tiene
        // aggiornato — mai l'id.
        destinatario = chatCorrente.teamName || "";
      } else if (tipo === TIPO_RUOLO) {
        destinatario = chatCorrente.ruoloBersaglio || "";
      } else if (tipo === TIPO_DIRETTA) {
        const membri = Array.isArray(chatCorrente.membri)
          ? chatCorrente.membri.map(String)
          : [];

        destinatario =
          membri.find((id) => id !== String(soggettoCorrente)) ||
          chatCorrente.destinatario ||
          "";
      }

      // Il figlio si DICHIARA; il backend VERIFICA tutela e tessera. Vuoto
      // quando si scrive per sé — e sui due canali del club è vuoto sempre.
      const soggettoDichiarato =
        soggettoCorrente && soggettoCorrente !== MIO_ID ? soggettoCorrente : null;

      await api.post("/messages", {
        tipo,
        destinatario,
        soggettoDichiarato,
        testo: input,
        // Del messaggio citato viaggia il solo id: il testo lo rilegge il
        // server dal messaggio vero.
        rispostaA: replyTo ? { messageId: replyTo.id } : null
      }, { timeoutMs: TIMEOUT_LUNGO_MS });

      setReplyTo(null);
      setInput("");
      // La conversazione ora esiste davvero: l'intenzione ha esaurito il suo
      // compito e l'elenco la porterà da sé.
      setChatPendente(null);
    } catch (err) {
      // FINE DEL RIFIUTO MUTO: un 401/403/409/429 aveva lo stesso aspetto di un
      // invio riuscito — il campo si svuotava e non succedeva nulla. Ora il
      // testo resta dov'è e il motivo si legge. I testi del server sono già in
      // inglese alla fonte: non si traducono.
      console.error("Send error:", err?.status || err);

      let motivo = "";

      try {
        motivo = JSON.parse(err?.body || "{}").error || "";
      } catch {
        motivo = "";
      }

      // D8B-FIX: a tetto scaduto il messaggio può essere già salvato (e le
      // push partite): non si invita a ripetere.
      setSendError(
        err && err.timeout === true
          ? TIMEOUT_INVIO_MESSAGE
          : motivo || "Message not sent. Please try again."
      );
    } finally {
      setSending(false);
    }
  };

  const cancellaMessaggio = async (messageId) => {
    try {
      // Chi può cancellare lo decide il server dal token: l'autore reale del
      // messaggio o un amministratore.
      await api.post("/messages/delete", { chatId: activeChat, messageId });
    } catch (err) {
      console.error("Delete refused:", err?.status || err);
      setSendError("Message not deleted.");
    }

    setOpenMenuId(null);
  };

  // METTE O TOGLIE una reazione, a nome del soggetto con cui si scrive ora
  // (Fetta 3b). Il soggetto si dichiara come nell'invio: vuoto quando si
  // reagisce per sé. Il «togli» usa lo STESSO soggetto del «metti», perché il
  // backend toglie la voce della coppia (reale, mostrato).
  // ⚠️ NESSUN AGGIORNAMENTO OTTIMISTICO: la pastiglia cambia quando il
  // documento cambia davvero (`onSnapshot`). Un rifiuto — 403, 429 «Too many
  // reactions» — lascia la pastiglia com'era e ne dice il motivo.
  const reagisci = async (messageId, chiave, giaMia) => {
    setOpenMenuId(null);
    setSceltaReazioneId(null);

    if (!activeChat || !messageId) return;

    try {
      setSendError("");

      await api.post("/messages/react", {
        chatId: activeChat,
        messageId,
        reazione: chiave,
        azione: giaMia ? "remove" : "add",
        soggettoDichiarato:
          soggettoCorrente && soggettoCorrente !== MIO_ID ? soggettoCorrente : null
      });
    } catch (err) {
      console.error("Reaction refused:", err?.status || err);

      let motivo = "";

      try {
        motivo = JSON.parse(err?.body || "{}").error || "";
      } catch {
        motivo = "";
      }

      // A tetto scaduto la reazione può essere stata salvata comunque: lo dirà
      // la pastiglia stessa, che segue il documento.
      setSendError(
        err && err.timeout === true
          ? "The reaction may not have been saved. Please check before trying again."
          : motivo || "Reaction not saved. Please try again."
      );
    }
  };

  // CHI HA REAGITO — si chiede al server al tocco di una pastiglia. I nomi
  // vengono SOLO da GET /messages/reactions, che dà il `mostrato` (per un
  // minore, il bambino) già filtrato come la rubrica: mai dal `reale` del
  // documento.
  const apriElencoReazioni = async (messageId) => {
    if (!activeChat || !messageId) return;

    setOpenMenuId(null);
    setSceltaReazioneId(null);
    setElencoReazioni({ messageId, stato: "caricamento", reazioni: {} });

    // Si aggiorna solo se la finestra mostra ancora QUESTO messaggio.
    const seAncoraAperta = (nuovo) => (attuale) =>
      attuale && attuale.messageId === messageId ? nuovo : attuale;

    try {
      const risposta = await api.get(
        `/messages/reactions?chatId=${encodeURIComponent(activeChat)}` +
          `&messageId=${encodeURIComponent(messageId)}`
      );

      const reazioni =
        risposta && risposta.reazioni && typeof risposta.reazioni === "object"
          ? risposta.reazioni
          : {};

      setElencoReazioni(seAncoraAperta({ messageId, stato: "pronto", reazioni }));
    } catch (err) {
      console.error("Reactions list error:", err?.status || err);
      setElencoReazioni(seAncoraAperta({ messageId, stato: "errore", reazioni: {} }));
    }
  };

  // ===========================================================================
  // L'USCITA — home del cappello attivo
  // ===========================================================================
  const esci = () => {
    const ruolo = canonicalRole(activeContext?.role || user?.role);

    const rotta =
      ruolo === "SUPERADMIN" ? "/home/homesuperadmin" :
      ruolo === "ADMIN" ? "/home/homeadmin" :
      ruolo === "COACH" ? "/home/homecoach" :
      ruolo === "PARENT" ? "/home/homeparent" :
      ruolo === "PLAYER" ? "/home/homeplayer" :
      "/home";

    navigate(rotta);
  };

  // ===========================================================================
  // IL «BACK» DELLA BANDA §7.2 — UNA SOLA «Back» IN PAGINA
  // ===========================================================================
  // Il doc 07 §14.11 dice che questa pagina usa «la barra utente e la banda
  // "Back" (su mobile, entrando in una conversazione)»: la banda del telaio e
  // la «Back» della chat sono LA STESSA COSA, non due bande sovrapposte. Il
  // Back della banda cambia quindi significato con la schermata — NavBand
  // accetta una FUNZIONE proprio per questo (NavBand.jsx, gia' usata da
  // View Profile e da My Calendar):
  //   - mobile, dentro la conversazione o il pannello info: torna indietro DI
  //     UNA schermata (info → conversazione → elenco), §14.2;
  //   - altrimenti (desktop, o mobile sull'elenco): esce dalla pagina, verso
  //     la home del cappello attivo — e' l'«Exit» di prima.
  const indietro = () => {
    if (compatto && vista !== "elenco") {
      setVista(vista === "info" ? "conversazione" : "elenco");
      return;
    }

    esci();
  };

  // ===========================================================================
  // LA META DI UNA CHAT — il nome, il ruolo, l'avatar, la categoria
  // ===========================================================================
  const metaDellaChat = useCallback(
    (chat) => {
      if (!chat) return null;

      if (chat.tipo === TIPO_ANNUNCIO) {
        return {
          nome: chat.titoloMostrato || "Announcements",
          sottotitolo: RUOLO_ISTITUZIONALE,
          iniziale: "AN",
          photoUrl: "",
          photoUserId: "",
          gruppo: true,
          categoria: "",
          annuncio: true
        };
      }

      if (chat.tipo === TIPO_RUOLO) {
        return {
          // ⚠️ `titoloMostrato` di un gruppo di ruolo è il ruolo CANONICO, cioè
          // tutto maiuscolo: a schermo va l'etichetta leggibile (doc 07 §3.2).
          nome: roleLabel(chat.ruoloBersaglio || chat.titoloMostrato),
          sottotitolo: RUOLO_ISTITUZIONALE,
          iniziale: "RO",
          photoUrl: "",
          photoUserId: "",
          gruppo: true,
          categoria: "",
          annuncio: false
        };
      }

      if (chat.tipo === TIPO_SQUADRA) {
        const nome = chat.teamName || chat.titoloMostrato || "Team";

        return {
          nome,
          sottotitolo: "Team",
          // La sigla del gruppo: le prime lettere del nome squadra (§14.3).
          iniziale: nome.replace(/[^A-Za-z0-9]/g, "").slice(0, 3).toUpperCase(),
          photoUrl: "",
          photoUserId: "",
          gruppo: true,
          categoria: categoriaDi(nome),
          annuncio: false
        };
      }

      // DIRETTA — il titolo dipende da chi guarda: è il nome dell'ALTRO, e per
      // un tutore «l'altro» si legge rispetto al figlio, non a sé.
      const membri = Array.isArray(chat.membri) ? chat.membri.map(String) : [];

      const miei = new Set([String(MIO_ID), ...idsFigli.map(String)]);

      const altro =
        membri.find((id) => !miei.has(id)) ||
        String(chat.destinatario || "") ||
        "";

      const persona = risolviPersona(altro);

      return {
        nome: persona.nome,
        sottotitolo: persona.etichettaRuolo,
        iniziale: persona.iniziale,
        photoUrl: persona.photoUrl,
        photoUserId: persona.photoUserId,
        gruppo: false,
        categoria: "",
        annuncio: false
      };
    },
    [MIO_ID, idsFigli, risolviPersona, categoriaDi]
  );

  // ===========================================================================
  // L'ELENCO A SEZIONI
  // ===========================================================================
  const chatVisibili = useMemo(() => {
    let elenco = chatList;

    // GLOBAL VIEW — filtro di PRESENTAZIONE, non una query diversa: l'elenco
    // arriva comunque intero (un ADMIN è nei `lettori` di tutti i gruppi).
    //
    // ⚠️ SPENTA, NASCONDE UNA COSA SOLA: le DIRETTE FRA TERZI, cioè quelle in
    // cui non compaio né io né un mio figlio. I gruppi, i canali di ruolo e
    // l'annuncio restano: un amministratore li vede a titolo proprio, non
    // «guardando le chat degli altri».
    //
    // ⚠️ Il metro NON può essere «sono fra i `membri`»: il SUPERADMIN sta nei
    // `lettori` dei gruppi ma NON fra i loro `membri`, e con quel metro si
    // ritroverebbe un elenco vuoto.
    // ⚠️ I DUE COMANDI ADMIN VALGONO SOLO SOTTO IL CAPPELLO ADMIN. I bottoni
    // che li accendono spariscono col cappello membro (barra admin più sotto),
    // ma lo STATO resta acceso: senza questa lettura, un admin che accende
    // Global View e poi passa al cappello PARENT continuerebbe a vedere le
    // dirette fra terzi con un cappello che non ne ha titolo. Si spengono qui,
    // dove il filtro si applica, non dove il bottone si disegna.
    const globalViewAttiva = vistaAmministrativa && globalView;
    const filtroRuoliAttivo = vistaAmministrativa && filtroRuoli;

    if (!globalViewAttiva) {
      const miei = new Set([String(MIO_ID), ...idsFigli.map(String)]);

      elenco = elenco.filter((chat) => {
        if (chat.tipo !== TIPO_DIRETTA) return true;

        const membri = Array.isArray(chat.membri) ? chat.membri.map(String) : [];

        return membri.some((id) => miei.has(id));
      });
    }

    if (filtroRuoliAttivo) {
      elenco = elenco.filter((chat) => chat.tipo === TIPO_RUOLO);
    }

    const cerca = filtroElenco.trim().toLowerCase();

    if (cerca) {
      elenco = elenco.filter((chat) =>
        (metaDellaChat(chat)?.nome || "").toLowerCase().includes(cerca)
      );
    }

    return elenco;
  }, [
    chatList,
    globalView,
    filtroRuoli,
    vistaAmministrativa,
    filtroElenco,
    MIO_ID,
    idsFigli,
    metaDellaChat
  ]);

  // «Your children's teams» / «Your team» / «Teams» dipendono dal LEGAME, non
  // dal ruolo dichiarato: si incrociano i `membri` del gruppo coi propri figli.
  //
  // ⚠️ IL TIMONE È PASSATO DAL PERMESSO AL CAPPELLO. Fino a ieri il ramo
  // «Your team» chiedeva `!eAmministratore` — un PERMESSO — e la sezione
  // «Roles» non aveva gate alcuno: un admin col cappello PARENT vedeva
  // ancora tutto. Adesso la barra mostra ciò che appartiene al cappello che si
  // porta, e SOLO quello:
  //
  //   cappello ADMIN/SUPERADMIN → tutto, come prima, senza filtro di squadra
  //   cappello PARENT           → «Your children's teams»
  //   cappello COACH/PLAYER     → «Your team»
  //
  // Restano fuori dal cappello, e si vedono sempre: «Announcements» (il canale
  // del club parla a chiunque) e «Direct» (le proprie conversazioni non sono
  // un attributo del ruolo — nasconderle sotto un cappello vorrebbe dire
  // togliere a un genitore i suoi messaggi perché sta guardando da allenatore).
  // Fuori dal cappello anche «Admin Staff», per D2, che è un blocco a parte.
  //
  // ⚠️ Il filtro NON è un permesso: le chat arrivano già filtrate dai
  // `lettori` che ha composto il backend. Qui si sceglie solo che cosa
  // MOSTRARE di ciò a cui si ha comunque titolo.
  const sezioni = useMemo(() => {
    const figli = new Set(idsFigli.map(String));

    const gruppi = {
      figli: [],
      mia: [],
      squadre: [],
      annunci: [],
      ruoli: [],
      dirette: []
    };

    // Vero se il gruppo di squadra appartiene alle squadre del cappello.
    // `squadreCappello === null` = il cappello non nomina squadre → non filtra.
    const squadraDelCappello = (chat) => {
      if (!squadreCappello) return true;

      return squadreCappello.has(normalizza(chat.teamName || chat.titoloMostrato));
    };

    for (const chat of chatVisibili) {
      const membri = Array.isArray(chat.membri) ? chat.membri.map(String) : [];

      if (chat.tipo === TIPO_SQUADRA) {
        const dEiFigli = membri.some((id) => figli.has(id));
        const mia = membri.includes(String(MIO_ID));

        if (vistaAmministrativa) {
          // Sotto il cappello amministrativo la barra è quella di prima: le
          // squadre in cui si è membri non sono «la propria squadra», sono
          // parte del club che si amministra.
          if (dEiFigli) gruppi.figli.push(chat);
          else gruppi.squadre.push(chat);
        } else if (!squadraDelCappello(chat)) {
          // Squadra estranea al cappello: non si mostra affatto.
          continue;
        } else if (cappelloGenitore) {
          if (dEiFigli) gruppi.figli.push(chat);
        } else if (cappelloAdulto) {
          if (mia) gruppi.mia.push(chat);
        } else {
          // Nessun cappello riconoscibile (utenza senza ruolo utile): si tiene
          // il comportamento di prima, che non toglie nulla a nessuno.
          if (dEiFigli) gruppi.figli.push(chat);
          else if (mia) gruppi.mia.push(chat);
          else gruppi.squadre.push(chat);
        }
      } else if (chat.tipo === TIPO_ANNUNCIO) {
        gruppi.annunci.push(chat);
      } else if (chat.tipo === TIPO_RUOLO) {
        // PUNTO 1 — «Roles» è una sezione del cappello amministrativo, e di
        // nessun altro. Sotto un cappello membro non si disegna.
        //
        // ⚠️ È UN FILTRO DI VISTA, NON UN TAGLIO DELLA CONSEGNA: i `lettori`
        // del gruppo non si toccano e la push continua ad arrivare. Chi
        // riceve un annuncio di ruolo lo riceve; semplicemente non ha in barra
        // un canale che non sa amministrare.
        if (vistaAmministrativa) gruppi.ruoli.push(chat);
      } else {
        gruppi.dirette.push(chat);
      }
    }

    return [
      { chiave: "figli", titolo: "Your children's teams", chat: gruppi.figli },
      { chiave: "mia", titolo: "Your team", chat: gruppi.mia },
      { chiave: "squadre", titolo: "Teams", chat: gruppi.squadre },
      { chiave: "annunci", titolo: "Announcements", chat: gruppi.annunci },
      { chiave: "ruoli", titolo: "Roles", chat: gruppi.ruoli },
      { chiave: "dirette", titolo: "Direct", chat: gruppi.dirette }
    ].filter((sezione) => sezione.chat.length > 0);
  }, [
    chatVisibili,
    idsFigli,
    MIO_ID,
    vistaAmministrativa,
    cappelloGenitore,
    cappelloAdulto,
    squadreCappello
  ]);

  // ===========================================================================
  // «ADMIN STAFF» — sempre visibile a chiunque (D2)
  // ===========================================================================
  // ⚠️ IL SUPERADMIN È ESCLUSO: è un'utenza di servizio, non un interlocutore
  // dei soci. Il confronto si fa sul valore CANONICO, non sulla grafia grezza.
  const adminStaff = useMemo(() => {
    return persone
      .filter((persona) => {
        if (String(persona.id) === String(MIO_ID)) return false;
        if (String(persona.status || "ACTIVE").toUpperCase() !== "ACTIVE") return false;
        return canonicalRole(persona.role) === "ADMIN";
      })
      .sort((a, b) =>
        `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
      );
  }, [persone, MIO_ID]);

  // ===========================================================================
  // APRIRE UNA CONVERSAZIONE
  // ===========================================================================
  // `useCallback` senza dipendenze (tocca solo setter, che sono stabili): la
  // usa anche l'effetto dell'indirizzo qui sotto, che non deve ripartire a
  // ogni resa.
  const apriChat = useCallback((id) => {
    setActiveChat(id);
    setSendError("");
    setReplyTo(null);
    setEditMessage(null);
    setInput("");
    setVista("conversazione");
  }, []);

  // ===========================================================================
  // L'INDIRIZZO DICE LA VERITÀ SULLA CONVERSAZIONE A SCHERMO (D-05, modo A)
  // ===========================================================================
  // Nei due versi, con UN effetto solo (due effetti separati si rincorrerebbero
  // nella stessa resa: lo scrittore riscriverebbe la chat vecchia prima che
  // l'apertura chiesta dall'indirizzo arrivi allo stato).
  //   • INGRESSO — `/messaging?chat=<id>` (il tocco su una notifica: service
  //     worker e `useFCMDeviceToken`) apre `<id>` con `apriChat`. Si aspetta
  //     che rubrica ed elenco abbiano risposto, e l'effetto è DICHIARATO DOPO
  //     il reset del cappello: quel reset gira anche al primo montaggio e
  //     cancellerebbe un'apertura fatta nello stato iniziale. Se `<id>` non è
  //     nell'elenco decide la Via 2c (`getDoc`, la regola sul documento):
  //     nessun permesso nuovo qui. Ascoltando `location` vale anche a pagina
  //     già montata (una seconda notifica apre la chat nuova).
  //   • USCITA — a ogni cambio della conversazione davvero a schermo
  //     (`aSchermo`) l'indirizzo si riscrive: `?chat=<id>` se c'è, `/messaging`
  //     nudo altrimenti (elenco sul telefono, cambio di cappello). Con
  //     `replace`, mai `push`: la cronologia del browser non si riempie.
  // `chatUrlServita` è l'ultima chat dell'indirizzo già servita (aperta da qui,
  // o scritta da qui): così l'effetto non riapre la propria scrittura.
  const chatDallUrl = useMemo(
    () => new URLSearchParams(location.search).get("chat") || null,
    [location.search]
  );
  const chatUrlServita = useRef(null);
  const paginaPronta =
    rubricaStato === "errore" || elencoStato === "pronto" || elencoStato === "errore";

  useEffect(() => {
    if (!chatDallUrl) chatUrlServita.current = null;

    // INGRESSO
    if (chatDallUrl && chatDallUrl !== chatUrlServita.current) {
      // Finché la pagina non è pronta l'indirizzo NON si tocca: la richiesta
      // resta lì, in attesa di essere servita.
      if (!paginaPronta) return;

      chatUrlServita.current = chatDallUrl;

      if (!(aSchermo && activeChat === chatDallUrl)) {
        // `apriChat` cambia sempre `activeChat` o `vista`: l'effetto riparte e
        // la scrittura qui sotto trova l'indirizzo già allineato.
        apriChat(chatDallUrl);
        return;
      }
    }

    // USCITA
    const voluta = aSchermo ? activeChat : null;
    if (voluta === chatDallUrl) return;

    chatUrlServita.current = voluta;
    navigate(
      voluta ? `/messaging?chat=${encodeURIComponent(voluta)}` : "/messaging",
      { replace: true }
    );
  }, [chatDallUrl, paginaPronta, aSchermo, activeChat, apriChat, navigate]);

  // Una diretta con una persona: l'id è deterministico e lo compone la stessa
  // regola del backend (`idChatDiretta`) — le due parti sono il SOGGETTO
  // MOSTRATO e il destinatario, non «io» e il destinatario: per un tutore la
  // diretta è quella del FIGLIO.
  const apriDirettaCon = (idPersona) => {
    const soggetto = soggettiPerNuovaChat.includes(soggettoNuovaChat)
      ? String(soggettoNuovaChat)
      : String(soggettiPerNuovaChat[0] || "");

    if (!soggetto || !idPersona) return;

    const id = "user_" + [soggetto, String(idPersona)].sort().join("_");

    setChatPendente({
      id,
      tipo: TIPO_DIRETTA,
      destinatario: String(idPersona),
      membri: null
    });

    apriChat(id);
    setMostraSceltaPersona(false);
    setFiltroPersone("");
  };

  const apriAnnuncio = () => {
    const esistente = chatList.find((chat) => chat.tipo === TIPO_ANNUNCIO);

    if (esistente) {
      apriChat(esistente.id);
      return;
    }

    // L'annuncio nasce alla prima riga scritta: l'id è un fossile e non cambia.
    setChatPendente({ id: ID_ANNUNCIO, tipo: TIPO_ANNUNCIO, membri: null });
    apriChat(ID_ANNUNCIO);
  };

  // ===========================================================================
  // LA FINESTRA EMAIL — solo admin (D6)
  // ===========================================================================
  // Non invia: raccoglie gli indirizzi e apre il `mailto:` del client di posta.
  // Gli indirizzi arrivano dalla rotta di backend, non da un elenco letto dal
  // client: per un minore l'indirizzo è quello del TUTORE, mai del bambino, e
  // la rotta ha un gate di ruolo VERO (un non-admin riceve 403).
  const apriEmail = () => {
    setMostraEmail(true);
    setEmailScelti([]);
    setFiltroEmail("");
    setEmailStato("caricamento");
    setEmailErrore("");

    api
      .get("/chat/email-recipients")
      .then((dati) => {
        setEmailPersone(Array.isArray(dati?.persone) ? dati.persone : []);
        setEmailStato("pronte");
      })
      .catch((err) => {
        let motivo = "";

        try {
          motivo = JSON.parse(err?.body || "{}").error || "";
        } catch {
          motivo = "";
        }

        setEmailErrore(motivo || "Recipients could not be loaded.");
        setEmailStato("errore");
      });
  };

  const componiEmail = () => {
    const indirizzi = [];

    for (const id of emailScelti) {
      const persona = emailPersone.find((voce) => String(voce.id) === String(id));
      if (!persona) continue;

      for (const indirizzo of persona.emails || []) {
        if (indirizzo && !indirizzi.includes(indirizzo)) indirizzi.push(indirizzo);
      }
    }

    setMostraEmail(false);
    setEmailScelti([]);

    if (indirizzi.length === 0) return;

    window.location.href = `mailto:${indirizzi.join(";")}`;
  };

  // ===========================================================================
  // RESA — le date, le ore, i partecipanti
  // ===========================================================================
  const metaCorrente = metaDellaChat(chatCorrente);

  const oraBreve = (ts) => {
    if (!ts?.seconds) return "";

    const data = new Date(ts.seconds * 1000);
    const ora = new Date();

    if (data.toDateString() === ora.toDateString()) {
      return data.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
    }

    return data.toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit" });
  };

  const oraDelMessaggio = (ts) => {
    if (!ts?.seconds) return "";

    return new Date(ts.seconds * 1000).toLocaleTimeString("en-AU", {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // L'etichetta del separatore di data (§14.4): «Today», «Yesterday», o la
  // data. Inglese, come ogni testo a schermo (§3.7).
  const etichettaGiorno = (ts) => {
    if (!ts?.seconds) return "";

    const data = new Date(ts.seconds * 1000);
    const oggi = new Date();
    const ieri = new Date(oggi.getTime() - 24 * 60 * 60 * 1000);

    if (data.toDateString() === oggi.toDateString()) return "Today";
    if (data.toDateString() === ieri.toDateString()) return "Yesterday";

    return data.toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  // I partecipanti del pannello info (§14.2, §14.5).
  //
  // ⚠️ NEI DUE CANALI DEL CLUB NON SI ELENCA NESSUNO. I `membri` dell'annuncio
  // sono l'anagrafica intera e quelli di un gruppo di ruolo sono tutti i
  // giocatori del club — cioè quasi tutti i bambini: disegnarne l'elenco
  // restituirebbe a chiunque legge il registro nominativo che la rubrica
  // filtrata per competenza esiste per chiudere. Si dice quanti sono, e basta.
  const partecipanti = useMemo(() => {
    if (!chatCorrente) return [];
    if (chatCorrente.tipo === TIPO_ANNUNCIO || chatCorrente.tipo === TIPO_RUOLO) return [];

    const membri = Array.isArray(chatCorrente.membri) ? chatCorrente.membri : [];

    return membri.map((id) => risolviPersona(id));
  }, [chatCorrente, risolviPersona]);

  const numeroMembri = Array.isArray(chatCorrente?.membri)
    ? chatCorrente.membri.length
    : 0;

  const personeSelezionabili = persone
    .filter((persona) => String(persona.id) !== String(MIO_ID))
    .filter((persona) => String(persona.status || "ACTIVE").toUpperCase() === "ACTIVE")
    .filter((persona) => !RUOLI_NON_PERSONA.includes(canonicalRole(persona.role)))
    .filter((persona) =>
      `${persona.firstName} ${persona.lastName}`
        .toLowerCase()
        .includes(filtroPersone.toLowerCase())
    );

  const emailSelezionabili = emailPersone
    .filter((persona) => !RUOLI_NON_PERSONA.includes(canonicalRole(persona.role)))
    .filter((persona) =>
      `${persona.firstName} ${persona.lastName}`
        .toLowerCase()
        .includes(filtroEmail.toLowerCase())
    );

  // Quale pannello si disegna: sopra la soglia tutti e tre, sotto uno solo.
  const mostraElenco = !compatto || vista === "elenco";
  const mostraConversazione = !compatto || vista === "conversazione";
  const mostraInfo = compatto ? vista === "info" : Boolean(activeChat);

  // ===========================================================================
  // LA RESA
  // ===========================================================================
  return (
    <div className="lc">

      {/* ------------------------------------------------------------- */}
      {/* TELAIO CONDIVISO §7 — barra utente §7.1 + banda §7.2            */}
      {/* ------------------------------------------------------------- */}
      {/* Si MONTANO i due componenti condivisi, non se ne riscrive uno:
          la barra utente (logo del club nel cerchio oro, nome, email,
          Logout) e la banda (bordo oro F10) devono essere LE STESSE di
          ogni altra pagina, e cambiare in un punto solo. Stanno in testa,
          sopra il layout della chat, che non cambia di una riga. */}
      <UserBar />
      <NavBand backTo={indietro} />

      {/* ------------------------------------------------------------- */}
      {/* BARRA SUPERIORE — nessun comando admin, per nessun ruolo        */}
      {/* ------------------------------------------------------------- */}
      <div className="lc__bar">
        {/* Meta' sinistra VUOTA di proposito: il «Back»/«Exit» che stava
            qui e' passato alla banda §7.2 qui sopra (§14.11), cosi' che
            in pagina ci sia UNA sola «Back», sempre nello stesso punto
            come in ogni altra pagina (§7.2). Il gruppo resta perche'
            `.lc__bar` e' `space-between`: senza, «New chat» scivolerebbe
            a sinistra e la barra cambierebbe forma. */}
        <div className="lc__bar-group" />

        <div className="lc__bar-group">
          <button
            className="lc__btn"
            onClick={() => setMostraSceltaPersona(true)}
          >
            <FaPlus aria-hidden="true" /> New chat
          </button>
        </div>
      </div>

      {/* ------------------------------------------------------------- */}
      {/* BARRA ADMIN — §14.6: visibile SOLO ad ADMIN/SUPERADMIN         */}
      {/* ------------------------------------------------------------- */}
      {/* ⚠️ «Email» in particolare, fino a ieri, la vedeva chiunque: era
          il buco che D6 chiude. Il bottone nascosto è cortesia; la difesa
          vera è il 403 della rotta. */}
      {/* ⚠️ E ORA È IL CAPPELLO A DECIDERLO, non più il solo permesso: un
          amministratore che sta guardando da genitore non ha davanti gli
          attrezzi dell'amministrazione. Resta cortesia, non difesa — il 403
          della rotta non si è mosso di un millimetro. */}
      {vistaAmministrativa && (
        <div className="lc__adminbar">
          <button
            className={`lc__btn ${globalView ? "lc__btn--on" : ""}`}
            onClick={() => setGlobalView((prima) => !prima)}
          >
            <FaGlobe aria-hidden="true" />
            {globalView ? "Exit Global View" : "Global View"}
          </button>

          <button
            className={`lc__btn ${filtroRuoli ? "lc__btn--on" : ""}`}
            onClick={() => setFiltroRuoli((prima) => !prima)}
          >
            <FaUsers aria-hidden="true" /> Roles
          </button>

          <button className="lc__btn" onClick={apriAnnuncio}>
            <FaBullhorn aria-hidden="true" /> Announcements
          </button>

          <button className="lc__btn" onClick={apriEmail}>
            <FaEnvelope aria-hidden="true" /> Email
          </button>
        </div>
      )}

      {/* Le due custom property le legge il foglio SOLO da 768px in su
          (LIVECHAT-DESK, Fetta 2): sul telefono sono inerti. */}
      <div
        ref={corpoRef}
        className={`lc__body${trascinamento ? " lc__body--resizing" : ""}`}
        style={{
          [VARIABILE_COLONNA.list]: `${colonne.list}px`,
          [VARIABILE_COLONNA.info]: `${colonne.info}px`
        }}
      >

        {/* ----------------------------------------------------------- */}
        {/* PANNELLO 1 — ELENCO CHAT (§14.3)                             */}
        {/* ----------------------------------------------------------- */}
        {mostraElenco && (
          <aside className="lc__list">
            <div className="lc__list-head">
              <input
                type="text"
                className="lc__search"
                placeholder="Search conversations…"
                value={filtroElenco}
                onChange={(e) => setFiltroElenco(e.target.value)}
              />
            </div>

            <div className="lc__scroll">
              {rubricaStato === "caricamento" && (
                <div className="lc__empty">Loading your chats…</div>
              )}

              {rubricaStato === "errore" && (
                <div className="lc__error" role="alert">{rubricaErrore}</div>
              )}

              {rubricaStato === "pronta" && elencoStato === "caricamento" && (
                <div className="lc__empty">Loading your chats…</div>
              )}

              {elencoStato === "errore" && (
                <div className="lc__error" role="alert">{elencoErrore}</div>
              )}

              {rubricaStato === "pronta" &&
                elencoStato === "pronto" &&
                sezioni.length === 0 && (
                  <div className="lc__empty">
                    No chats yet. Your team groups appear here.
                  </div>
                )}

              {sezioni.map((sezione) => (
                <React.Fragment key={sezione.chiave}>
                  <div className="lc__section">{sezione.titolo}</div>

                  {sezione.chat.map((chat) => {
                    const meta = metaDellaChat(chat);
                    const nonLetti = chat.nonLetti?.[MIO_ID] || 0;

                    return (
                      <button
                        key={chat.id}
                        type="button"
                        className={`lc__row ${activeChat === chat.id ? "lc__row--on" : ""}`}
                        onClick={() => apriChat(chat.id)}
                      >
                        {meta.photoUrl && meta.photoUserId ? (
                          <AuthAvatar
                            className="lc__avatar"
                            userId={meta.photoUserId}
                            photoKey={meta.photoUrl}
                            alt="avatar"
                            fallback={<span className="lc__avatar">{meta.iniziale}</span>}
                          />
                        ) : (
                          <span
                            className={`lc__avatar ${meta.gruppo ? "lc__avatar--group" : ""}`}
                          >
                            {meta.iniziale}
                          </span>
                        )}

                        <span className="lc__row-text">
                          <span className="lc__row-top">
                            <span className="lc__name" title={meta.nome}>{meta.nome}</span>
                            <span className="lc__time">{oraBreve(chat.ultimoQuando)}</span>
                          </span>

                          <span className="lc__row-bottom">
                            {meta.annuncio ? (
                              <span className="lc__badge">Announcement</span>
                            ) : meta.categoria ? (
                              <PastigliaCategoriaSeNota category={meta.categoria} />
                            ) : null}

                            {/* IL RUOLO SOTTO OGNI NOME (§14.5). */}
                            {meta.sottotitolo && (
                              <span className="lc__role">{meta.sottotitolo}</span>
                            )}

                            <span className="lc__preview">{chat.ultimoTesto || ""}</span>

                            {nonLetti > 0 && (
                              <span className="lc__unread">{nonLetti}</span>
                            )}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </React.Fragment>
              ))}

              {/* «Admin staff» SEMPRE in fondo e SEMPRE visibile, per ogni
                  ruolo (D2). Non è un comando admin: è una sezione. */}
              {adminStaff.length > 0 && (
                <>
                  <div className="lc__section">Admin staff</div>

                  {adminStaff.map((persona) => (
                    <button
                      key={persona.id}
                      type="button"
                      className="lc__row"
                      onClick={() => apriDirettaCon(persona.id)}
                    >
                      <AuthAvatar
                        className="lc__avatar"
                        userId={persona.id}
                        photoKey={persona.photoUrl || ""}
                        alt="avatar"
                        fallback={
                          <span className="lc__avatar">
                            {(persona.firstName || PERSONA_SCONOSCIUTA)
                              .charAt(0)
                              .toUpperCase()}
                          </span>
                        }
                      />

                      <span className="lc__row-text">
                        <span className="lc__row-top">
                          <span
                            className="lc__name"
                            title={`${persona.firstName} ${persona.lastName}`}
                          >
                            {persona.firstName} {persona.lastName}
                          </span>
                        </span>

                        <span className="lc__row-bottom">
                          <span className="lc__role">{roleLabel(persona.role)}</span>
                        </span>
                      </span>
                    </button>
                  ))}
                </>
              )}
            </div>
          </aside>
        )}

        {/* MANIGLIA 1 — fra elenco e conversazione (LIVECHAT-DESK, Fetta 2).
            Mai sotto la soglia compatta; e il foglio la accende solo con
            `(hover: hover) and (pointer: fine)`, cioè col mouse. Non è un
            <button>, per non ereditare la cascata globale `button {}`. */}
        {!compatto && mostraElenco && (
          <div
            className={`lc__handle${
              trascinamento && trascinamento.colonna === "list" ? " lc__handle--active" : ""
            }`}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize chat list"
            title="Drag to resize · double-click to reset"
            onPointerDown={(e) => inizioTrascinamento(e, "list")}
            onDoubleClick={() => ripristinaColonna("list")}
          />
        )}

        {/* ----------------------------------------------------------- */}
        {/* PANNELLO 2 — CONVERSAZIONE (§14.4)                           */}
        {/* ----------------------------------------------------------- */}
        {mostraConversazione && (
          <section className="lc__thread">
            {!activeChat ? (
              <div className="lc__empty">
                Pick a conversation on the left to start reading.
              </div>
            ) : (
              <>
                <div
                  className="lc__thread-head"
                  onClick={() => setVista(compatto ? "info" : vista)}
                >
                  <span
                    className={`lc__avatar ${metaCorrente?.gruppo ? "lc__avatar--group" : ""}`}
                  >
                    {metaCorrente?.iniziale}
                  </span>

                  <span className="lc__thread-title">
                    <span className="lc__row-top">
                      <span className="lc__name" title={metaCorrente?.nome}>
                        {metaCorrente?.nome}
                      </span>
                    </span>

                    <span className="lc__row-bottom">
                      {metaCorrente?.categoria && (
                        <PastigliaCategoriaSeNota category={metaCorrente.categoria} />
                      )}
                      <span className="lc__role">{metaCorrente?.sottotitolo}</span>
                    </span>
                  </span>

                  {compatto && (
                    <span className="lc__menu-btn" aria-hidden="true">
                      <FaInfoCircle />
                    </span>
                  )}
                </div>

                <div
                  className="lc__messages"
                  ref={agganciaScorritore}
                  onScroll={(e) => {
                    setOpenMenuId(null);
                    // Se chi guarda sta in fondo: lo legge lo scorrimento
                    // automatico quando cambiano le sole reazioni.
                    const c = e.currentTarget;
                    inFondoRef.current =
                      c.scrollHeight - c.scrollTop - c.clientHeight <= SOGLIA_IN_FONDO_PX;
                  }}
                >
                  {messaggiStato === "caricamento" && (
                    <div className="lc__empty">Loading messages…</div>
                  )}

                  {messaggiStato === "errore" && (
                    <div className="lc__error" role="alert">{messaggiErrore}</div>
                  )}

                  {messaggiStato === "pronti" && messages.length === 0 && (
                    <div className="lc__empty">No messages yet.</div>
                  )}

                  {messages.map((msg, indice) => {
                    // ⚠️ LE DUE IDENTITÀ DEL MESSAGGIO. La bolla sta a destra se
                    // l'ha scritta chi ha fatto l'accesso (`autoreReale`); il
                    // NOME che vi compare è quello del soggetto MOSTRATO — il
                    // bambino, quando un tutore ha scritto a nome suo (D1).
                    const mio = String(msg.autoreReale) === MIO_ID;

                    // IL CANALE DECIDE IL MITTENTE, NON LA PERSONA: in annuncio
                    // e ruolo la bolla si firma col club.
                    const istituzionale =
                      chatCorrente?.tipo === TIPO_ANNUNCIO ||
                      chatCorrente?.tipo === TIPO_RUOLO;

                    const mittente = istituzionale
                      ? {
                          nome: MITTENTE_ISTITUZIONALE,
                          etichettaRuolo: RUOLO_ISTITUZIONALE
                        }
                      : risolviPersona(msg.soggettoMostrato);

                    // G7 — sul PROPRIO messaggio, quando si è scritto a nome di
                    // un figlio, l'etichetta ricorda a nome di chi. Si accende
                    // sui due CAMPI del messaggio, non sullo stato del browser:
                    // altrimenti comparirebbe anche sui messaggi vecchi scritti
                    // a nome proprio. Agli altri arriva il solo nome del
                    // bambino.
                    const comeFiglio =
                      mio &&
                      !istituzionale &&
                      String(msg.soggettoMostrato) !== String(msg.autoreReale);

                    const precedente = indice > 0 ? messages[indice - 1] : null;

                    // SEPARATORE DI DATA (§14.4): quando il giorno cambia.
                    const giornoNuovo =
                      !precedente ||
                      etichettaGiorno(precedente.quando) !== etichettaGiorno(msg.quando);

                    // RAGGRUPPAMENTO PER MITTENTE (§14.4): l'intestazione
                    // compare solo sulla prima bolla di una serie.
                    const continua =
                      !giornoNuovo &&
                      precedente &&
                      String(precedente.autoreReale) === String(msg.autoreReale) &&
                      String(precedente.soggettoMostrato) === String(msg.soggettoMostrato);

                    const scaduto = msg.quando?.seconds
                      ? Date.now() - msg.quando.seconds * 1000 > 5 * 60 * 1000
                      : false;

                    // Le spunte: una consegnato, due letto. L'autore non si conta.
                    // CHI È ATTESO ALLA LETTURA (D-05, L2-bis): lo dice il
                    // messaggio stesso, `attesiDa` — gli id-documento dei
                    // destinatari, autore già escluso dal backend. È la stessa
                    // lingua di `lettoDa`: si confrontano senza tradurre.
                    // ⚠️ VUOTO e ASSENTE non sono la stessa cosa:
                    //  · `attesiDa: []` → nessuno era atteso: niente verde e
                    //    niente «N/M», resta la spunta di «inviato»;
                    //  · campo assente (messaggi di prima, o risoluzione caduta
                    //    sul server) → ripiego sul metro di prima, i `membri`
                    //    della chat (non su `participants`, che è morto).
                    const lettoDaMsg = Array.isArray(msg.lettoDa) ? msg.lettoDa : [];

                    let attesi;
                    let letti;

                    if (Array.isArray(msg.attesiDa)) {
                      const attesiSet = new Set(msg.attesiDa.map(String));
                      // Per prudenza l'autore si toglie comunque.
                      attesiSet.delete(String(msg.autoreReale));

                      attesi = attesiSet.size;
                      letti = new Set(
                        lettoDaMsg.map(String).filter((id) => attesiSet.has(id))
                      ).size;
                    } else {
                      const membriChat = Array.isArray(chatCorrente?.membri)
                        ? chatCorrente.membri
                        : [];

                      attesi = Math.max(membriChat.length - 1, 0);
                      letti = lettoDaMsg.filter(
                        (id) => String(id) !== String(msg.autoreReale)
                      ).length;
                    }

                    const tuttiLetti = attesi > 0 && letti >= attesi;

                    // Le pastiglie, dal documento (Fetta 3b).
                    const pastiglie = reazioniNellaChat
                      ? pastiglieDelMessaggio(msg.reazioni, MIO_ID, soggettoCorrente)
                      : [];

                    return (
                      <React.Fragment key={msg.id}>
                        {giornoNuovo && etichettaGiorno(msg.quando) && (
                          <div className="lc__day">{etichettaGiorno(msg.quando)}</div>
                        )}

                        <div
                          className={`lc__bubble-wrap ${
                            mio ? "lc__bubble-wrap--mine" : "lc__bubble-wrap--them"
                          }`}
                        >
                          <div
                            className={`lc__bubble ${
                              mio ? "lc__bubble--mine" : "lc__bubble--them"
                            } ${continua ? "lc__bubble--follow" : ""}`}
                          >
                            {!continua && !mio && (
                              <div className="lc__sender">
                                <span className="lc__sender-name">{mittente.nome}</span>
                                {mittente.etichettaRuolo && (
                                  <span className="lc__sender-role">
                                    {mittente.etichettaRuolo}
                                  </span>
                                )}
                              </div>
                            )}

                            {!continua && comeFiglio && (
                              <div className="lc__sender">
                                <span className="lc__sender-name">
                                  You · as {risolviPersona(msg.soggettoMostrato).nome}
                                </span>
                              </div>
                            )}

                            {msg.rispostaA && (
                              <div className="lc__quote">
                                <span className="lc__quote-name">
                                  {istituzionale
                                    ? MITTENTE_ISTITUZIONALE
                                    : risolviPersona(msg.rispostaA.soggettoMostrato).nome}
                                </span>
                                {msg.rispostaA.testo}
                              </div>
                            )}

                            {msg.testo}

                            {msg.modificato && (
                              <span className="lc__edited">(edited)</span>
                            )}

                            <div className="lc__meta">
                              <span>{oraDelMessaggio(msg.quando)}</span>

                              {mio && (
                                <span
                                  className={`lc__ticks ${tuttiLetti ? "lc__ticks--read" : ""}`}
                                  title={attesi > 1 ? `Read ${letti}/${attesi}` : undefined}
                                >
                                  {letti > 0 ? (
                                    <FaCheckDouble aria-hidden="true" />
                                  ) : (
                                    <FaCheck aria-hidden="true" />
                                  )}
                                  {attesi > 1 ? ` ${letti}/${attesi}` : ""}
                                </span>
                              )}

                              <button
                                type="button"
                                className="lc__menu-btn"
                                aria-label="Message actions"
                                onClick={() => {
                                  // Il menu si riapre sempre sulle voci, non
                                  // sulla fila delle sei.
                                  setSceltaReazioneId(null);
                                  setOpenMenuId(openMenuId === msg.id ? null : msg.id);
                                }}
                              >
                                <FaEllipsisV aria-hidden="true" />
                              </button>
                            </div>
                          </div>

                          {/* LE PASTIGLIE (Fetta 3b, doc 07 §14.8): sotto la
                              bolla, IN FLUSSO dentro il wrap — così il menu,
                              che è assoluto e ancorato al wrap
                              (`.lc__menu--sotto`), si apre sotto di esse e non
                              le sposta. Il tocco apre l'elenco dei nomi; non
                              reagisce (si reagisce da «React»). */}
                          {pastiglie.length > 0 && (
                            <div className="lc__reactions">
                              {pastiglie.map(({ chiave, Icona, etichetta, conteggio, mia }) => (
                                <button
                                  key={chiave}
                                  type="button"
                                  className={`lc__reaction ${mia ? "lc__reaction--mine" : ""}`}
                                  aria-label={`${etichetta}: ${conteggio}${
                                    mia ? ", including yours" : ""
                                  }. Show who reacted`}
                                  onClick={() => apriElencoReazioni(msg.id)}
                                >
                                  <Icona className="lc__reaction-icon" aria-hidden="true" />
                                  <span className="lc__reaction-count">{conteggio}</span>
                                </button>
                              ))}
                            </div>
                          )}

                          {openMenuId === msg.id && sceltaReazioneId === msg.id && (
                            // LA FILA DELLE SEI: prende il posto delle voci nello
                            // stesso riquadro del menu. Toccare una faccina già
                            // «mia» la toglie.
                            <div
                              ref={menuRef}
                              className={`lc__menu lc__menu--${versoMenu} lc__react-row`}
                              role="group"
                              aria-label="React"
                            >
                              {REAZIONI.map(({ chiave, Icona, etichetta }) => {
                                const giaMia = pastiglie.some(
                                  (p) => p.chiave === chiave && p.mia
                                );

                                return (
                                  <button
                                    key={chiave}
                                    type="button"
                                    className={`lc__react-pick ${
                                      giaMia ? "lc__react-pick--mine" : ""
                                    }`}
                                    aria-label={giaMia ? `Remove ${etichetta}` : `React with ${etichetta}`}
                                    aria-pressed={giaMia}
                                    onClick={() => reagisci(msg.id, chiave, giaMia)}
                                  >
                                    <Icona aria-hidden="true" />
                                  </button>
                                );
                              })}
                            </div>
                          )}

                          {openMenuId === msg.id && sceltaReazioneId !== msg.id && (
                            <div ref={menuRef} className={`lc__menu lc__menu--${versoMenu}`}>
                              <div
                                className="lc__menu-item"
                                onClick={() => {
                                  setReplyTo({ id: msg.id, testo: msg.testo });
                                  setOpenMenuId(null);
                                }}
                              >
                                Reply
                              </div>

                              {puoReagire && (
                                <div
                                  className="lc__menu-item"
                                  onClick={() => setSceltaReazioneId(msg.id)}
                                >
                                  React
                                </div>
                              )}

                              {/* Oltre i 5 minuti «Modify» resta a chi POSSIEDE
                                  il ruolo admin (`eAmministratore`, come il
                                  server in POST /messages/edit), e solo sui
                                  propri messaggi. */}
                              {mio && (!scaduto || eAmministratore) && (
                                <div
                                  className="lc__menu-item"
                                  onClick={() => {
                                    setEditMessage(msg);
                                    setInput(msg.testo);
                                    setOpenMenuId(null);
                                  }}
                                >
                                  Modify message
                                </div>
                              )}

                              {/* «Delete» è un attrezzo dell'amministrazione:
                                  segue il cappello come la barra. Il permesso
                                  vero resta al backend
                                  (`eAutore || eAmministratore`). */}
                              {vistaAmministrativa && (
                                <div
                                  className="lc__menu-item lc__menu-item--danger"
                                  onClick={() => cancellaMessaggio(msg.id)}
                                >
                                  Delete
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>

                {sendError && (
                  <div className="lc__error" role="alert">{sendError}</div>
                )}

                {replyTo && (
                  <div className="lc__strip">
                    <span>Replying to: {replyTo.testo}</span>
                    <button className="lc__btn" onClick={() => setReplyTo(null)}>
                      Cancel
                    </button>
                  </div>
                )}

                {editMessage && (
                  <div className="lc__strip">
                    <span>Editing message</span>
                    <button className="lc__btn" onClick={() => setEditMessage(null)}>
                      Cancel
                    </button>
                  </div>
                )}

                {soggettiPossibili.length === 0 ? (
                  <div className="lc__strip">
                    <span>You can read this conversation, but not write in it.</span>
                  </div>
                ) : (
                  <div className="lc__compose">
                    {/* IL SELETTORE DEL FIGLIO — compare SOLO quando le
                        possibilità sono più d'una (un genitore con due figli
                        nello stesso gruppo). Con una sola possibilità non si
                        chiede nulla. */}
                    {soggettiPossibili.length > 1 && (
                      <select
                        className="lc__subject"
                        aria-label="Write on behalf of"
                        value={soggettoCorrente}
                        onChange={(e) =>
                          setSoggettoScelto((prima) => ({
                            ...prima,
                            [activeChat]: e.target.value
                          }))
                        }
                      >
                        {soggettiPossibili.map((id) => (
                          <option key={id} value={id}>
                            {etichettaSoggetto(id)}
                          </option>
                        ))}
                      </select>
                    )}

                    <textarea
                      className="lc__input"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      placeholder="Write a message…"
                      rows={1}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          inviaMessaggio();
                        }
                      }}
                    />

                    <button
                      type="button"
                      className="lc__btn lc__btn--gold"
                      onClick={inviaMessaggio}
                      disabled={sending}
                    >
                      {sending ? "Sending…" : "Send"}
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* MANIGLIA 2 — fra conversazione e partecipanti: esiste solo
            quando esiste il pannello partecipanti (stessa condizione). */}
        {!compatto && mostraInfo && activeChat && (
          <div
            className={`lc__handle${
              trascinamento && trascinamento.colonna === "info" ? " lc__handle--active" : ""
            }`}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize participants panel"
            title="Drag to resize · double-click to reset"
            onPointerDown={(e) => inizioTrascinamento(e, "info")}
            onDoubleClick={() => ripristinaColonna("info")}
          />
        )}

        {/* ----------------------------------------------------------- */}
        {/* PANNELLO 3 — INFO PARTECIPANTI (§14.2, §14.5)                */}
        {/* ----------------------------------------------------------- */}
        {mostraInfo && activeChat && (
          <aside className="lc__info">
            <div className="lc__info-head">In this conversation</div>

            {partecipanti.length === 0 ? (
              <div className="lc__empty">
                {numeroMembri > 0
                  ? `${numeroMembri} club members receive this channel.`
                  : "No members to show yet."}
              </div>
            ) : (
              partecipanti.map((persona, indice) => (
                <div className="lc__member" key={`${persona.id}-${indice}`}>
                  <AuthAvatar
                    className="lc__avatar"
                    userId={persona.photoUserId}
                    photoKey={persona.photoUrl}
                    alt="avatar"
                    fallback={<span className="lc__avatar">{persona.iniziale}</span>}
                  />

                  <span className="lc__member-text">
                    <span className="lc__member-name" title={persona.nome}>
                      {persona.nome}
                    </span>
                    {/* Il ruolo sotto ogni nome, anche qui (§14.5). Di un
                        minore compaiono nome e ruolo, mai un recapito: la
                        rubrica non li porta. */}
                    <span className="lc__member-role">{persona.etichettaRuolo}</span>
                  </span>
                </div>
              ))
            )}
          </aside>
        )}
      </div>

      {/* --------------------------------------------------------------- */}
      {/* FINESTRA «CHI HA REAGITO» (Fetta 3b)                             */}
      {/* --------------------------------------------------------------- */}
      {/* I nomi sono quelli di GET /messages/reactions: il `mostrato` (per un
          minore, il bambino), «Club member» se la rubrica non lo darebbe.
          Mai il `reale`. Le righe di una faccina coincidono col numero della
          sua pastiglia: entrambi contano i `mostrato` distinti. */}
      {elencoReazioni && (
        <div className="lc__overlay">
          <div
            className="lc__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="lc-reactions-title"
          >
            <h3 className="lc__modal-title" id="lc-reactions-title">Reactions</h3>

            <div className="lc__modal-list">
              {elencoReazioni.stato === "caricamento" && (
                <div className="lc__empty">Loading reactions…</div>
              )}

              {elencoReazioni.stato === "errore" && (
                <div className="lc__empty">Reactions are not available right now.</div>
              )}

              {elencoReazioni.stato === "pronto" &&
                REAZIONI.every(
                  ({ chiave }) =>
                    !Array.isArray(elencoReazioni.reazioni[chiave]) ||
                    elencoReazioni.reazioni[chiave].length === 0
                ) && <div className="lc__empty">No reactions yet.</div>}

              {elencoReazioni.stato === "pronto" &&
                REAZIONI.map(({ chiave, Icona, etichetta }) => {
                  const righe = Array.isArray(elencoReazioni.reazioni[chiave])
                    ? elencoReazioni.reazioni[chiave]
                    : [];

                  if (righe.length === 0) return null;

                  return (
                    <div key={chiave} className="lc__reactors">
                      <div className="lc__reactors-head">
                        <Icona className="lc__reaction-icon" aria-hidden="true" />
                        <span>
                          {etichetta} · {righe.length}
                        </span>
                      </div>

                      {righe.map((riga, i) => (
                        <div key={`${chiave}-${riga.id || "x"}-${i}`} className="lc__reactor">
                          <span>{riga.nome || PERSONA_SCONOSCIUTA}</span>
                          {riga.ruolo && (
                            <span className="lc__contact-role">{roleLabel(riga.ruolo)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
            </div>

            <div className="lc__modal-actions">
              <button className="lc__btn" onClick={() => setElencoReazioni(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --------------------------------------------------------------- */}
      {/* FINESTRA «NEW CHAT» — una diretta con una persona                */}
      {/* --------------------------------------------------------------- */}
      {mostraSceltaPersona && (
        <div className="lc__overlay">
          <div className="lc__modal">
            <h3 className="lc__modal-title">Start a conversation</h3>

            {soggettiPerNuovaChat.length > 1 && (
              <select
                className="lc__subject"
                aria-label="Write on behalf of"
                value={
                  soggettiPerNuovaChat.includes(soggettoNuovaChat)
                    ? soggettoNuovaChat
                    : soggettiPerNuovaChat[0]
                }
                onChange={(e) => setSoggettoNuovaChat(e.target.value)}
              >
                {soggettiPerNuovaChat.map((id) => (
                  <option key={id} value={id}>
                    {etichettaSoggetto(id)}
                  </option>
                ))}
              </select>
            )}

            <input
              type="text"
              className="lc__search"
              placeholder="Type to filter…"
              value={filtroPersone}
              onChange={(e) => setFiltroPersone(e.target.value)}
            />

            <div className="lc__modal-list">
              {personeSelezionabili.length === 0 ? (
                <div className="lc__empty">No one to show here yet.</div>
              ) : (
                personeSelezionabili.map((persona) => (
                  <button
                    key={persona.id}
                    type="button"
                    className="lc__contact"
                    onClick={() => apriDirettaCon(persona.id)}
                  >
                    <span>
                      {persona.firstName} {persona.lastName}
                    </span>
                    <span className="lc__contact-role">{roleLabel(persona.role)}</span>
                  </button>
                ))
              )}
            </div>

            <div className="lc__modal-actions">
              <button
                className="lc__btn"
                onClick={() => {
                  setMostraSceltaPersona(false);
                  setFiltroPersone("");
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --------------------------------------------------------------- */}
      {/* FINESTRA «EMAIL» — SOLO ADMIN (§14.9, D6)                        */}
      {/* --------------------------------------------------------------- */}
      {/* ⚠️ Doppio gate: il bottone che la apre esiste solo per un
          amministratore, e la rotta che porta gli indirizzi rifiuta con 403
          chiunque non lo sia. Il primo è cortesia, il secondo è la difesa. */}
      {mostraEmail && vistaAmministrativa && (
        <div className="lc__overlay">
          <div className="lc__modal">
            <h3 className="lc__modal-title">Select email recipients</h3>

            <input
              type="text"
              className="lc__search"
              placeholder="Type to filter…"
              value={filtroEmail}
              onChange={(e) => setFiltroEmail(e.target.value)}
            />

            <div className="lc__modal-list">
              {emailStato === "caricamento" && (
                <div className="lc__empty">Loading recipients…</div>
              )}

              {emailStato === "errore" && (
                <div className="lc__error" role="alert">{emailErrore}</div>
              )}

              {emailStato === "pronte" && emailSelezionabili.length === 0 && (
                <div className="lc__empty">No recipients to show.</div>
              )}

              {emailStato === "pronte" &&
                emailSelezionabili.map((persona) => (
                  <button
                    key={persona.id}
                    type="button"
                    className={`lc__contact ${
                      emailScelti.includes(persona.id) ? "lc__contact--on" : ""
                    }`}
                    onClick={() =>
                      setEmailScelti((ids) =>
                        ids.includes(persona.id)
                          ? ids.filter((voce) => voce !== persona.id)
                          : [...ids, persona.id]
                      )
                    }
                  >
                    {/* A schermo il NOME e il ruolo. Gli indirizzi non si
                        mostrano: restano nel `mailto:`, e per un minore sono
                        quelli del tutore. */}
                    <span>
                      {persona.firstName} {persona.lastName}
                    </span>
                    <span className="lc__contact-role">{roleLabel(persona.role)}</span>
                  </button>
                ))}
            </div>

            <div className="lc__modal-actions">
              <button
                className="lc__btn"
                onClick={() => {
                  setMostraEmail(false);
                  setEmailScelti([]);
                }}
              >
                Cancel
              </button>

              <button
                className="lc__btn lc__btn--gold"
                disabled={emailScelti.length === 0}
                onClick={componiEmail}
              >
                Open in mail
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
